import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { MultiSelect } from 'primereact/multiselect';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import { InputNumberAtom } from '../../atoms/InputNumber/InputNumberAtom';
import LocalizedInputAtom from '../../atoms/LocalizedInput/LocalizedInputAtom';
import type { LocalizedValue } from '../../atoms/LocalizedInput/LocalizedInputAtom';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import TextareaAtom from '../../atoms/Textarea/TextareaAtom';
// `atoms/StatusBadge/` (PascalCase dir) — NOT `atoms/status-badge/` (kebab-case dir), a
// same-named but unrelated component (props: `status: StatusValue` + `variant`, used by
// Atlas's own renderer, `Node.tsx`). This editor uses the `tone` + `label` API below.
import StatusBadgeAtom, { StatusBadgeTone } from '../../atoms/StatusBadge/StatusBadgeAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import DialogWrapper from '../../molecules/DialogWrapper/DialogWrapper';
import ContextLevelSummaryAtom from '../../atoms/ContextLevelSummary/ContextLevelSummaryAtom';
import NotificationBadgeAtom from '../../atoms/NotificationBadge/NotificationBadgeAtom';
import LevelOverrideEditorMolecule from '../../molecules/LevelOverrideEditor/LevelOverrideEditorMolecule';
import PerspectivePickerMolecule from '../../molecules/PerspectivePicker/PerspectivePickerMolecule';
import AspectCatalogMolecule from '../../molecules/AspectCatalog/AspectCatalogMolecule';
import { propertyOptionsToCatalogEntries } from '../../../utils/catalogBuilders';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import OverlayPanelAtom, { type OverlayPanelHandle } from '../../atoms/OverlayPanel/OverlayPanelAtom';
import AtlasLivePreviewMolecule from '../../molecules/Atlas/AtlasLivePreview/AtlasLivePreviewMolecule';
import ReorderableListMolecule from '../../molecules/ReorderableList/ReorderableListMolecule';
import type { ContextLevel } from 'types/context';
import type { LevelDisplayOverride } from 'types/contextView';
import type { UnifiedFilters } from 'types/filters';
import type { PerspectiveCatalogGroup } from '../../../utils/filterPropertyBuilder';
import type {
  PropertyType,
  PropertyOption as CatalogPropertyOption,
} from '../../molecules/PropertyFilter/PropertyFilterMolecule';
import { aspectKindFromPropertyType } from '../../../utils/aspectKind';
import { getFilterTypeVisual } from '../../../utils/filterTypeVisuals';
import './ContextViewEditorOrganism.scss';

/** Card density a context view can render at. */
type CardViewDensity = 'minimal' | 'standard' | 'expanded';

// ============================================================================
// Types
// ============================================================================

export type ViewTypeValue = 'atlas' | 'treemap' | 'kanban' | 'table' | 'timeline' | 'scorecard';

export type CardDensity = CardViewDensity;

export interface BaseConfigFormState {
  visibleProperties?: string[];
  cardDensity?: CardDensity;
  showStatus?: boolean;
  showFlow?: boolean;
  showAvatar?: boolean;
  levelOverrides?: Record<string, LevelDisplayOverride>;
  /**
   * Present because `viewRecordToViewForm` loads `base_config` wholesale, NOT
   * because this editor authors filters — it has no filter UI. Declared so the
   * round-trip is visible in the type rather than laundered through a cast;
   * `viewFormToUpdatePayload` strips it so a stale overlay snapshot cannot
   * clobber a shared-filter commit (EAINT-789).
   */
  levelFilters?: Record<string, UnifiedFilters>;
  /** The view-wide twin of `levelFilters` — loaded, never authored here, stripped on save. */
  globalFilters?: UnifiedFilters;
}

/**
 * The molecule vocabulary Atlas actually renders (see `atlas-header.tsx`, `Node.tsx`).
 *
 * This used to read `'NodeLabel' | 'PerspectiveSelector' | 'FlowStage' | 'SphereProperty'`,
 * of which only the first two exist downstream: `FlowStage` and `SphereProperty` were never
 * renderer types, so a column of either kind drew nothing. Meanwhile every type stored in
 * real views but absent from that union — `ApproachBadges`, `NodeCounts`, `NodeDetailsButton`,
 * `NodePerspectivePill`, `AtlasDateSelector` — was invisible to the editor and silently
 * dropped out of the list it displayed.
 *
 * `Aspect` (renamed from `SphereProperty` — EAINT-866; the wire format was never migrated
 * for this type because it had zero stored instances) is the design's aspect-bound column.
 * Its renderer (`AtlasSpherePropertyMolecule`) landed in EAINT-857 Phase 4; the grouped,
 * scope-aware aspect picker (Phase 6) landed after, so it is addable.
 */
export type AtlasMoleculeType =
  | 'NodeLabel'
  | 'NodeDetailsButton'
  | 'NodeCounts'
  | 'NodePerspectivePill'
  | 'ApproachBadges'
  | 'PerspectiveSelector'
  | 'ArcStageSelector'
  | 'FlowStageSelector'
  | 'Aspect';

export interface AtlasColumnFormState {
  type: AtlasMoleculeType;
  /**
   * `important` (renderer name: "keep when space is tight") lives here, not as a
   * column-level field — `Node.tsx:421` and `atlas-header.tsx:469` both read
   * `moleculeProps.important`. It used to be written column-level
   * (`AtlasColumnFormState.important`), a key nothing downstream ever read; the
   * toggle for it has never actually worked. There is no editor-level `width` field
   * either — `AtlasColumnFormState.width` was written but nothing renders it.
   */
  moleculeProps?: Record<string, unknown>;
}

export interface AtlasConfigFormState {
  type: 'atlas';
  /**
   * The molecule row Atlas renders. THIS is the stored field — `atlas-header.tsx`,
   * `AtlasTree.tsx` and `Node.tsx` all read `nodeMolecules`, and every Atlas view in the
   * database stores it.
   *
   * EAINT-833 pointed this editor at a `columns` field instead. `columns` had been written
   * once by the Jan-2026 template migration and then deliberately retired by
   * `20260609100000_regenerate_atlas_context_views` — whose header calls it "the broken
   * columns[] shape", because flattening a molecule to `{ property, renderer }` discarded
   * every `moleculeProps` value. The editor therefore read a field no view has, showed
   * "0 columns" for all of them, and wrote to a key nothing renders. Reading the real
   * field restores every existing view; no data migration is required, because the stored
   * shape `{ type, moleculeProps }` is already exactly `AtlasColumnFormState`.
   */
  nodeMolecules?: AtlasColumnFormState[];
  showTraversalArrows?: boolean;
  allowTraversalDirectionChange?: boolean;
  showNearbyNodeCount?: boolean;
  lockFocus?: boolean;
  /**
   * Editor-only — NOT part of the canonical `AtlasViewConfig` (`types/contextView.d.ts`).
   * Renders the "Urgency" built-in toggle for authoring; nothing downstream currently
   * consumes this key under this name. Declared explicitly here (rather than left implicit
   * via a spread) so the divergence from the stored shape is visible at the type level.
   */
  showUrgency?: boolean;
  hideAddSphere?: boolean;
  mobileBreakpoint?: number;
}

export interface TreeMapConfigFormState {
  type: 'treemap';
  defaultCardView?: CardViewDensity;
  allowCardViewToggle?: boolean;
  columnWidth?: number;
  columnGap?: number;
  allowDragDrop?: boolean;
  showMinimap?: boolean;
}

export interface KanbanConfigFormState {
  type: 'kanban';
  laneProperty: string;
  laneOrder?: string[];
  swimlaneProperty?: string;
  cardFields?: string[];
  cardSize?: 'compact' | 'standard' | 'large';
  allowDragBetweenLanes?: boolean;
  allowDragBetweenSwimlanes?: boolean;
}

export interface TableColumnFormState {
  property: string;
  header: LocalizedValue;
  width?: number | 'auto';
  sortable?: boolean;
  filterable?: boolean;
  renderer?: 'text' | 'date' | 'status' | 'avatar' | 'progress' | 'custom';
}

export interface TableConfigFormState {
  type: 'table';
  columns: TableColumnFormState[];
  groupBy?: string;
  groupCollapsed?: boolean;
  rowHeight?: 'compact' | 'standard' | 'comfortable';
  allowColumnReorder?: boolean;
  allowColumnResize?: boolean;
  allowRowSelection?: boolean;
  pageSize?: number;
}

export interface TimelineConfigFormState {
  type: 'timeline';
  startDateProperty: string;
  endDateProperty?: string;
  groupBy?: string;
  defaultScale?: 'day' | 'week' | 'month' | 'quarter' | 'year';
  allowScaleChange?: boolean;
  showDependencies?: boolean;
  showProgress?: boolean;
  showMilestones?: boolean;
}

export interface ScorecardConfigFormState {
  type: 'scorecard';
  surfaceColumns: { showStatus: boolean; showVelocity: boolean; showGravity: boolean };
  metricColumns: string[];
  defaultSort: { column: string; direction: 'asc' | 'desc' };
  groupBy?: string;
  display: 'table' | 'cards';
  defaultPerspective?: string;
}

export type ViewConfigFormState =
  | AtlasConfigFormState
  | TreeMapConfigFormState
  | KanbanConfigFormState
  | TableConfigFormState
  | TimelineConfigFormState
  | ScorecardConfigFormState;

export interface ContextViewFormState {
  system_name: string;
  name: LocalizedValue;
  description: LocalizedValue;
  contextRef: string;
  viewType: ViewTypeValue;
  baseConfig: BaseConfigFormState;
  viewConfig: ViewConfigFormState;
  /**
   * Whether this view is visible to the organization beyond its creator.
   * Read-only here — display state only. Changed exclusively through the
   * listing action (`onToggleListing`), never through `onChange`, since it's
   * a permission-gated resource action, not a form field.
   */
  listed?: boolean;
}

export interface ContextOption {
  label: string;
  value: string;
}

/**
 * The org aspect catalog option, under this editor's historical field name:
 * `value` is `CatalogPropertyOption.key`. Everything else is inherited rather
 * than re-declared, because every previous attempt to list the fields this
 * editor "needs" has under-listed them (EAINT-899). The picker's taxonomy
 * (`group`/`groupLabel`) and the aspect coordinates (`aspectSystemName`,
 * `aspectIdentity`, `aspectFamily`, `definedOn`) were all dropped by a
 * `{label, value}` remap in `ContextViewEditorOverlayOrganism`, so the Atlas
 * aspect picker rendered one flat unsectioned list of every aspect in the
 * organization while the filter editor's rail — fed from the same
 * `useAspectCatalog` — rendered it tiered by aspect family.
 *
 * Extending the catalog type means a field added there reaches this editor
 * without a second edit, and a lossy remap fails to compile instead of
 * silently degrading a picker.
 */
export interface PropertyOption extends Omit<CatalogPropertyOption, 'key' | 'type'> {
  /** The property `name` — `CatalogPropertyOption.key` under this editor's name. */
  value: string;
  /**
   * Optional here, unlike on the catalog option. Options built outside the aspect catalog —
   * legacy `{label, value}` callers — have no kind, and `aspectPickerProperties` defaults them
   * to `text` at the boundary. Widening it back to required would reject those callers.
   */
  type?: PropertyType;
}

export interface ContextViewEditorOrganismProps {
  value: ContextViewFormState;
  onChange: (value: ContextViewFormState) => void;
  /** Human-readable name of the context this view renders (locked, set on creation). */
  contextDisplayName?: string;
  contextOptions?: ContextOption[];
  propertyOptions?: PropertyOption[];
  /** True while `propertyOptions` (the aspect catalog) is still being fetched — the aspect picker renders skeleton rows instead of "no results". */
  propertyOptionsLoading?: boolean;
  /** Non-null when the aspect catalog failed to load — the aspect picker renders a retry affordance. */
  propertyOptionsError?: string | null;
  /** Retries the aspect catalog fetch. Rendered as the aspect picker's "Try again" when `propertyOptionsError` is set. */
  onRetryPropertyOptions?: () => void;
  /**
   * `system_name`s of the sphere types this context view actually walks — scopes the
   * aspect picker's out-of-scope annotation the same way `buildPerspectiveCatalog`'s
   * `scopedTypeSystemNames` scopes the perspective picker. Empty/absent means "unknown":
   * every aspect renders in-scope rather than flagged without cause.
   */
  aspectScopeTypeSystemNames?: string[];
  /**
   * The grouped perspective catalog for the `PerspectiveSelector` column's binding
   * picker (see `buildPerspectiveCatalog`). Empty/absent renders the picker with no
   * options rather than falling back to free text — callers that mount this editor
   * for Atlas views must supply it.
   */
  perspectiveCatalog?: PerspectiveCatalogGroup[];
  /** The context's levels, for the level-overrides rail (card E). Empty when the context has no levels yet. */
  levels?: ContextLevel[];
  disabled?: boolean;
  /** When true, locks the view type to its current value and renders it as a static card. */
  lockViewType?: boolean;
  /** @deprecated System name is derived from the view name; no longer rendered as an input. */
  systemNameEditable?: boolean;
  /**
   * Whether the caller may list/unlist this view (`publish` on the view —
   * requires edit on its parent context). When false, the list/unlist
   * action is hidden; the visibility chip still renders either way.
   */
  canPublish?: boolean;
  /** Fired when the user confirms a list/unlist action. Absent while `canPublish` is false — no action is rendered to trigger it. */
  onToggleListing?: (next: boolean) => void;
  /** Whether the caller currently has a personalization overlay on the rendered board (Card A "Reset my adjustments" affordance). Absent/false hides the row entirely. */
  hasPersonalizationOverlay?: boolean;
  /** Fired when the user confirms resetting their overlay. Absent while `hasPersonalizationOverlay` is false — no action is rendered to trigger it. */
  onResetPersonalization?: () => void;
  /** Navigate to the context editor for the view's context. When provided, an "Open context" link appears next to the locked context chip in Card A. */
  onOpenContext?: () => void;
  /**
   * The context's root sphere id, threaded down from the page (`AtlasComponent`
   * has it in scope; the editor itself only knows `contextRef`). Passed to the
   * Atlas live preview so `useAvailableTypes` resolves a real sphere instead of
   * firing a doomed request for a fabricated preview id.
   */
  rootSphereId?: string;
  className?: string;
}

// ============================================================================
// View-type metadata (icons drive the View type card / tiles)
// ============================================================================

const VIEW_TYPE_META: Record<ViewTypeValue, { icon: string }> = {
  treemap: { icon: 'pi pi-th-large' },
  atlas: { icon: 'pi pi-compass' },
  kanban: { icon: 'pi pi-server' },
  table: { icon: 'pi pi-table' },
  timeline: { icon: 'pi pi-calendar' },
  scorecard: { icon: 'pi pi-chart-bar' },
};

// ============================================================================
// Atlas molecule catalog (static metadata — not i18n, labels come from t())
// ============================================================================

/** One option in a `segmented`-type prop's `SegmentedControlAtom`. */
interface AtlasMoleculePropOption {
  value: string;
  i18nLabel: string;
}

export interface AtlasMoleculePropDef {
  key: string;
  i18nLabel: string;
  type: 'number' | 'text' | 'segmented' | 'toggle' | 'perspective' | 'aspect';
  defaultValue: unknown;
  min?: number;
  max?: number;
  /** Required when `type === 'segmented'`. */
  options?: AtlasMoleculePropOption[];
}

export interface AtlasMoleculeMeta {
  icon: string;
  /** Text character rendered instead of the pi icon (e.g. "T" for NodeLabel). */
  glyph?: string;
  props: AtlasMoleculePropDef[];
  locked?: boolean;
  /**
   * Offered in the "Add column" menu. False for two different reasons, both of which
   * still require the type to EXIST here so stored molecules render in the list rather
   * than vanishing:
   *  - `locked`        — NodeLabel is added automatically and never removable.
   *  - fixed elements  — NodeCounts / NodePerspectivePill are toggles in the design, not
   *                      columns; they are shown read-only until they are migrated out of
   *                      the molecule array.
   * `Aspect` (the aspect column) is addable — its renderer (Phase 4) and grouped
   * picker (Phase 6) have both landed.
   */
  addable?: boolean;
  /** Reordering is meaningless for fixed-position elements; the design models these as toggles. */
  fixedElement?: boolean;
  /**
   * At most one column of this type may exist on the view. Wireframe: `MOLECULES[type].multi`
   * is absent (falsy) for `ArcStageSelector`/`ApproachBadges` — every other addable type sets
   * `multi: true` because repeatability is stated on every catalog row (one column per aspect,
   * or per perspective, is the point). The add-menu option is disabled and marked "Added" once
   * a column of this type already exists.
   */
  singleInstance?: boolean;
}

/**
 * Exported for the vocabulary guard test: the union is erased at runtime, so the
 * catalog's keys are the only enumerable statement of it. `Record<AtlasMoleculeType, ...>`
 * makes the two provably identical at compile time.
 */
export const ATLAS_MOLECULE_CATALOG: Record<AtlasMoleculeType, AtlasMoleculeMeta> = {
  NodeLabel: {
    icon: 'pi pi-tag',
    glyph: 'T',
    props: [
      { key: 'charLimit', i18nLabel: 'context.viewEditor.atlas.maxCharacters', type: 'number', defaultValue: 40, min: 10, max: 200 },
    ],
    locked: true,
    addable: false,
  },
  Aspect: {
    icon: 'pi pi-cog',
    props: [
      // `type: 'aspect'` drives a special-cased render block (like `perspective`):
      // a trigger + `AspectCatalogMolecule` popover, plus the bound aspect's
      // kind-specific option fields (`isFriendly`, `choiceStyle`) rendered
      // dynamically alongside it — see the `prop.type === 'aspect'` branch below.
      { key: 'aspect', i18nLabel: 'context.viewEditor.atlas.aspectPicker.fieldLabel', type: 'aspect', defaultValue: '' },
    ],
    // Renderer landed (EAINT-857 Phase 4 — AtlasSpherePropertyMolecule, registered in
    // Node.tsx / atlas-header.tsx). The grouped aspect picker (Phase 6) now
    // replaces the free-text `aspect` box, so this is addable.
    addable: true,
  },
  PerspectiveSelector: {
    icon: 'pi pi-user',
    props: [
      { key: 'perspectiveSystemName', i18nLabel: 'context.viewEditor.atlas.perspective', type: 'perspective', defaultValue: '' },
      {
        key: 'display',
        i18nLabel: 'context.viewEditor.atlas.perspectiveDisplay',
        type: 'segmented',
        defaultValue: 'avatar+name',
        options: [
          { value: 'avatar+name', i18nLabel: 'context.viewEditor.atlas.perspectiveDisplayAvatarName' },
          { value: 'avatar', i18nLabel: 'context.viewEditor.atlas.perspectiveDisplayAvatar' },
          { value: 'name', i18nLabel: 'context.viewEditor.atlas.perspectiveDisplayName' },
        ],
      },
      // Renderer support (EAINT-857 Phase 11): `AtlasPerspectiveSelector` forwards
      // `display`/`maxAvatars`/`placeholder` to `PerspectiveSelector` (single
      // cardinality) and `maxAvatars`/`placeholder` to `MultiSelectPerspectiveSelector`
      // (multi cardinality, via `PerspectiveAssigneesDisplayAtom`'s `maxVisible`).
      // `maxAvatars` has no visible effect on a single-cardinality column, which only
      // ever shows one assignee — that's an inherent property of cardinality, not dead
      // wiring (the same way `charLimit` has no effect on an already-short name).
      { key: 'maxAvatars', i18nLabel: 'context.viewEditor.atlas.maxAvatars', type: 'number', defaultValue: 3, min: 1, max: 10 },
      { key: 'assignable', i18nLabel: 'context.viewEditor.atlas.assignable', type: 'toggle', defaultValue: true },
      { key: 'placeholder', i18nLabel: 'context.viewEditor.atlas.whenEmptyShow', type: 'text', defaultValue: '' },
    ],
    addable: true,
  },
  ArcStageSelector: {
    icon: 'pi pi-arrow-right',
    props: [],
    addable: true,
    singleInstance: true,
  },
  ApproachBadges: {
    icon: 'pi pi-box',
    props: [],
    addable: true,
    singleInstance: true,
  },
  NodeDetailsButton: {
    icon: 'pi pi-align-left',
    props: [],
    // The design carries this as "Description" but keeps it out of the add menu.
    addable: false,
  },
  // Legacy alias for ArcStageSelector, still present in older stored views. Kept so those
  // molecules render in the list instead of falling through as unknown; never offered.
  FlowStageSelector: {
    icon: 'pi pi-arrow-right',
    props: [],
    addable: false,
  },
  // Fixed-position elements. The design models these as toggles, not columns — they are
  // shown read-only so authors can see what a view actually holds, and are neither addable
  // nor reorderable. Migrating them out of the molecule array is separate work.
  NodeCounts: {
    icon: 'pi pi-share-alt',
    props: [],
    addable: false,
    fixedElement: true,
  },
  NodePerspectivePill: {
    icon: 'pi pi-users',
    props: [],
    addable: false,
    fixedElement: true,
  },
};

// ============================================================================
// Aspect kind helpers — the kind badge reuses `filterEditor.panel.typeLabel`,
// the platform's existing `PropertyType` → localized-label map (already
// covers all eleven members in both locales), rather than a second one.
//
// `AtlasSpherePropertyMolecule` reads six kind-specific props today —
// `isFriendly` (date), `choiceStyle` (choice), `maxValues` (any multi-value
// aspect), `showAvatar` (reference), `numberFormat` (number) and, since
// EAINT-866, `characterLimit` (text) — all six are authorable here. The
// wireframe's remaining per-kind matrix (`boolStyle`,
// `personStyle`) still has no renderer support — exposing those would recreate
// defect class #1 this spec exists to fix ("settings that write where nothing
// reads"). See spec OQ-D.
// ============================================================================

// Each predicate is now a comparison against the SHARED resolver rather than its own type
// test. They used to be independent, and two of them disagreed with the renderer: this
// side called an aspect `choice` when its filter type was `enum`, while the renderer
// called it `choice` when it carried `metadata.options`. Both were right for their own
// input and the sets are disjoint, so the same aspect could render as a chip there and
// offer text options here. `aspectKindFromPropertyType` is the only thing that decides
// kind on this side now, and `aspectKind.spec.ts` pins it against the schema-side
// resolver so the two cannot drift apart again.
//
// `text` remains the FALLBACK kind, so it stays whatever the resolver does not otherwise
// claim — a positive type list would silently drop every string-ish type the schema model
// adds later, and those are exactly the aspects that need truncating.
const isDateAspectKind = (type?: PropertyType): boolean => {
  const kind = aspectKindFromPropertyType(type);
  return kind === 'date' || kind === 'datetime';
};
const isChoiceAspectKind = (type?: PropertyType): boolean =>
  aspectKindFromPropertyType(type) === 'choice';
const isReferenceAspectKind = (type?: PropertyType): boolean =>
  aspectKindFromPropertyType(type) === 'reference';
const isNumberAspectKind = (type?: PropertyType): boolean =>
  aspectKindFromPropertyType(type) === 'number';
const isTextAspectKind = (type?: PropertyType): boolean =>
  aspectKindFromPropertyType(type) === 'text';

// ============================================================================
// Constants
// ============================================================================

export function getDefaultViewConfig(viewType: ViewTypeValue): ViewConfigFormState {
  switch (viewType) {
    case 'atlas':
      return {
        type: 'atlas',
        nodeMolecules: [{ type: 'NodeLabel', moleculeProps: { charLimit: 40 } }],
        allowTraversalDirectionChange: true,
        showNearbyNodeCount: true,
        showUrgency: true,
        showTraversalArrows: true,
      };
    case 'treemap':
      return { type: 'treemap', defaultCardView: 'standard', allowCardViewToggle: true, columnWidth: 300, columnGap: 16 };
    case 'kanban':
      return { type: 'kanban', laneProperty: '', cardSize: 'standard', allowDragBetweenLanes: true };
    case 'table':
      return { type: 'table', columns: [], rowHeight: 'standard', pageSize: 50, allowColumnReorder: true, allowColumnResize: true };
    case 'timeline':
      return { type: 'timeline', startDateProperty: '', defaultScale: 'month', allowScaleChange: true };
    case 'scorecard':
      return {
        type: 'scorecard',
        surfaceColumns: { showStatus: true, showVelocity: true, showGravity: true },
        metricColumns: [],
        defaultSort: { column: 'name', direction: 'asc' },
        display: 'table',
      };
  }
}

export const DEFAULT_VIEW_FORM_STATE: ContextViewFormState = {
  system_name: '',
  name: { default: '' },
  description: { default: '' },
  contextRef: '',
  viewType: 'atlas',
  baseConfig: { cardDensity: 'standard', showStatus: true, showFlow: false, showAvatar: false },
  viewConfig: getDefaultViewConfig('atlas'),
  listed: false,
};

// ============================================================================
// Layout primitives (mirror the design's lettered ConfigCard + Row)
// ============================================================================

interface ConfigCardProps {
  /** Omitted for Atlas's single unlettered "Columns" card — every other caller still passes one. */
  letter?: string;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Stable React key for an Atlas column row, keyed on the column object's identity
 * rather than its position. `reorderAtlasColumn` splices the same objects into a new
 * array, so identity survives a reorder while the index does not — an index-derived
 * key would re-key every moved row, unmounting the very node that holds the keyboard
 * lift's focus and stranding the list mid-lift with no way to drop or cancel.
 */
const atlasColumnKeys = new WeakMap<AtlasColumnFormState, string>();
let atlasColumnKeySeq = 0;
function atlasColumnKey(col: AtlasColumnFormState): string {
  let key = atlasColumnKeys.get(col);
  if (!key) {
    atlasColumnKeySeq += 1;
    key = `atlas-col-${atlasColumnKeySeq}`;
    atlasColumnKeys.set(col, key);
  }
  return key;
}

/**
 * Carries a column identity onto the replacement object an immutable patch produces.
 * Without this, editing a column in the inspector (`{ ...col, ...patch }`) mints a NEW
 * identity, React unmounts and remounts that row, and any keyboard lift anchored to it
 * is stranded — the same failure an index-derived key would cause on reorder.
 */
function carryAtlasColumnKey(from: AtlasColumnFormState, to: AtlasColumnFormState): AtlasColumnFormState {
  atlasColumnKeys.set(to, atlasColumnKey(from));
  return to;
}

const ConfigCard: React.FC<ConfigCardProps> = ({ letter, title, subtitle, action, children }) => (
  <section className="context-view-editor__card">
    <header className="context-view-editor__card-header">
      {letter && <span className="context-view-editor__card-badge" aria-hidden="true">{letter}</span>}
      <div className="context-view-editor__card-heading">
        <h3 className="context-view-editor__card-title">{title}</h3>
        {subtitle && <p className="context-view-editor__card-subtitle">{subtitle}</p>}
      </div>
      {action && <div className="context-view-editor__card-action">{action}</div>}
    </header>
    <div className="context-view-editor__card-body">{children}</div>
  </section>
);

interface FieldRowProps {
  label: string;
  description?: string;
  children: React.ReactNode;
}

const FieldRow: React.FC<FieldRowProps> = ({ label, description, children }) => (
  <div className="context-view-editor__row">
    <div className="context-view-editor__row-label">
      <span className="context-view-editor__row-label-text">{label}</span>
      {description && <span className="context-view-editor__row-label-desc">{description}</span>}
    </div>
    <div className="context-view-editor__row-control">{children}</div>
  </div>
);

// ============================================================================
// Component
// ============================================================================

const ContextViewEditorOrganism: React.FC<ContextViewEditorOrganismProps> = ({
  value,
  onChange,
  contextDisplayName,
  propertyOptions = [],
  propertyOptionsLoading = false,
  propertyOptionsError = null,
  onRetryPropertyOptions,
  aspectScopeTypeSystemNames = [],
  perspectiveCatalog = [],
  levels = [],
  disabled = false,
  lockViewType = false,
  canPublish = false,
  onToggleListing,
  hasPersonalizationOverlay = false,
  onResetPersonalization,
  onOpenContext,
  rootSphereId,
  className = '',
}) => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const [addingProperty, setAddingProperty] = useState(false);
  const [newProperty, setNewProperty] = useState('');
  const [selectedLevelId, setSelectedLevelId] = useState<string | null>(null);
  const [resetDialogVisible, setResetDialogVisible] = useState(false);
  const [selectedAtlasColIdx, setSelectedAtlasColIdx] = useState<number | null>(0);
  const [addingAtlasColumn, setAddingAtlasColumn] = useState(false);
  const atlasAddTriggerRef = useRef<HTMLButtonElement>(null);
  /**
   * The perspective and aspect pickers are hosted by `OverlayPanelAtom` rather than a
   * hand-rolled popover: atomic-design Rule 3 forbids re-implementing backdrop / Escape /
   * click-outside per call site, and the hand-rolled pair had neither focus trap nor
   * focus return. The atom owns dismissal; these refs only drive open/close and hand
   * focus back to the trigger on hide.
   */
  const perspectivePickerOverlayRef = useRef<OverlayPanelHandle>(null);
  const perspectivePickerTriggerRef = useRef<HTMLButtonElement>(null);
  const aspectPickerOverlayRef = useRef<OverlayPanelHandle>(null);
  const aspectPickerTriggerRef = useRef<HTMLButtonElement>(null);

  /**
   * Dismiss the add-column popover and hand focus back to the trigger. Without
   * the focus return, closing via Escape leaves focus on a removed node and the
   * next Tab restarts from the top of the document.
   */
  const closeAtlasAddPopover = useCallback(() => {
    setAddingAtlasColumn(false);
    atlasAddTriggerRef.current?.focus();
  }, []);

  const closePerspectivePicker = useCallback(() => {
    perspectivePickerOverlayRef.current?.hide();
  }, []);

  const closeAspectPicker = useCallback(() => {
    aspectPickerOverlayRef.current?.hide();
  }, []);

  const handleConfirmResetPersonalization = useCallback(() => {
    setResetDialogVisible(false);
    onResetPersonalization?.();
    logAnalyticsEvent('view_personalization_reset', undefined, {
      contextRef: value.contextRef,
      viewSystemName: value.system_name || null,
    });
  }, [onResetPersonalization, logAnalyticsEvent, value.contextRef, value.system_name]);

  const VIEW_TYPE_OPTIONS = useMemo(() => [
    { label: t('context.viewEditor.viewTypes.atlas'), value: 'atlas' as const, icon: VIEW_TYPE_META.atlas.icon },
    { label: t('context.viewEditor.viewTypes.treemap'), value: 'treemap' as const, icon: VIEW_TYPE_META.treemap.icon },
    { label: t('context.viewEditor.viewTypes.kanban'), value: 'kanban' as const, icon: VIEW_TYPE_META.kanban.icon },
    { label: t('context.viewEditor.viewTypes.table'), value: 'table' as const, icon: VIEW_TYPE_META.table.icon },
    { label: t('context.viewEditor.viewTypes.timeline'), value: 'timeline' as const, icon: VIEW_TYPE_META.timeline.icon },
    { label: t('context.viewEditor.viewTypes.scorecard'), value: 'scorecard' as const, icon: VIEW_TYPE_META.scorecard.icon },
  ], [t]);

  const CARD_DENSITY_OPTIONS = useMemo(() => [
    { label: t('context.viewEditor.cardDensityOptions.minimal'), value: 'minimal' as const },
    { label: t('context.viewEditor.cardDensityOptions.standard'), value: 'standard' as const },
    { label: t('context.viewEditor.cardDensityOptions.expanded'), value: 'expanded' as const },
  ], [t]);

  const CARD_SIZE_OPTIONS = useMemo(() => [
    { label: t('context.viewEditor.cardSizeOptions.compact'), value: 'compact' as const },
    { label: t('context.viewEditor.cardSizeOptions.standard'), value: 'standard' as const },
    { label: t('context.viewEditor.cardSizeOptions.large'), value: 'large' as const },
  ], [t]);

  const ROW_HEIGHT_OPTIONS = useMemo(() => [
    { label: t('context.viewEditor.rowHeightOptions.compact'), value: 'compact' as const },
    { label: t('context.viewEditor.rowHeightOptions.standard'), value: 'standard' as const },
    { label: t('context.viewEditor.rowHeightOptions.comfortable'), value: 'comfortable' as const },
  ], [t]);

  const TIME_SCALE_OPTIONS = useMemo(() => [
    { label: t('context.viewEditor.timeScaleOptions.day'), value: 'day' as const },
    { label: t('context.viewEditor.timeScaleOptions.week'), value: 'week' as const },
    { label: t('context.viewEditor.timeScaleOptions.month'), value: 'month' as const },
    { label: t('context.viewEditor.timeScaleOptions.quarter'), value: 'quarter' as const },
    { label: t('context.viewEditor.timeScaleOptions.year'), value: 'year' as const },
  ], [t]);


  const TABLE_RENDERER_OPTIONS = useMemo(() => [
    { label: t('context.viewEditor.renderers.text'), value: 'text' as const },
    { label: t('context.viewEditor.renderers.date'), value: 'date' as const },
    { label: t('context.viewEditor.renderers.status'), value: 'status' as const },
    { label: t('context.viewEditor.renderers.avatar'), value: 'avatar' as const },
    { label: t('context.viewEditor.renderers.progress'), value: 'progress' as const },
    { label: t('context.viewEditor.renderers.custom'), value: 'custom' as const },
  ], [t]);

  // --- General field handlers ---

  const handleDescriptionChange = useCallback(
    (description: LocalizedValue) => onChange({ ...value, description }),
    [value, onChange]
  );

  const handleViewTypeChange = useCallback(
    (viewType: ViewTypeValue) => {
      setSelectedAtlasColIdx(0);
      setAddingAtlasColumn(false);
      onChange({
        ...value,
        viewType,
        viewConfig: getDefaultViewConfig(viewType),
      });
    },
    [value, onChange]
  );

  // --- Base config handlers ---

  const updateBaseConfig = useCallback(
    (patch: Partial<BaseConfigFormState>) => {
      onChange({ ...value, baseConfig: { ...value.baseConfig, ...patch } });
    },
    [value, onChange]
  );

  // --- Level-override handlers (card E) ---

  // Memoized: `?? {}` would mint a new reference every render, defeating the
  // useCallback on the two handlers below and re-rendering the override editor.
  const levelOverrides = useMemo(
    () => value.baseConfig.levelOverrides ?? {},
    [value.baseConfig.levelOverrides]
  );

  const updateLevelOverrideField = useCallback(
    <K extends keyof LevelDisplayOverride>(
      levelId: string,
      field: K,
      fieldValue: LevelDisplayOverride[K] | undefined
    ) => {
      const current = levelOverrides[levelId] ?? {};
      const next: Record<string, unknown> = { ...current };
      if (fieldValue === undefined) {
        delete next[field as string];
      } else {
        next[field as string] = fieldValue;
      }
      updateBaseConfig({
        levelOverrides: { ...levelOverrides, [levelId]: next as LevelDisplayOverride },
      });
      logAnalyticsEvent('treemap_level_override_set', undefined, {
        viewSystemName: value.system_name,
        levelId,
        field,
      });
    },
    [levelOverrides, updateBaseConfig, logAnalyticsEvent, value.system_name]
  );

  const resetLevelOverride = useCallback(
    (levelId: string) => {
      const next = { ...levelOverrides };
      delete next[levelId];
      updateBaseConfig({ levelOverrides: next });
    },
    [levelOverrides, updateBaseConfig]
  );

  const selectedLevel = useMemo(
    () => levels.find((level) => level.id === selectedLevelId) ?? levels[0] ?? null,
    [levels, selectedLevelId]
  );

  // Card density is stored on the treemap viewConfig (defaultCardView) — the same
  // field the live density toggle persists and the renderer reads on load. For
  // non-treemap fallbacks it falls back to baseConfig.cardDensity.
  const cardDensity =
    value.viewConfig.type === 'treemap'
      ? value.viewConfig.defaultCardView ?? 'standard'
      : value.baseConfig.cardDensity ?? 'standard';

  const setCardDensity = useCallback(
    (density: CardViewDensity) => {
      if (value.viewConfig.type === 'treemap') {
        onChange({ ...value, viewConfig: { ...value.viewConfig, defaultCardView: density } });
      } else {
        onChange({ ...value, baseConfig: { ...value.baseConfig, cardDensity: density } });
      }
    },
    [value, onChange]
  );

  const visibleProperties = value.baseConfig.visibleProperties ?? [];

  const removeVisibleProperty = useCallback(
    (property: string) => {
      updateBaseConfig({ visibleProperties: visibleProperties.filter((p) => p !== property) });
    },
    [updateBaseConfig, visibleProperties]
  );

  const commitNewProperty = useCallback(() => {
    const trimmed = newProperty.trim();
    if (trimmed && !visibleProperties.includes(trimmed)) {
      updateBaseConfig({ visibleProperties: [...visibleProperties, trimmed] });
    }
    setNewProperty('');
    setAddingProperty(false);
  }, [newProperty, visibleProperties, updateBaseConfig]);

  const remainingPropertyOptions = useMemo(
    () => propertyOptions.filter((o) => !visibleProperties.includes(o.value)),
    [propertyOptions, visibleProperties]
  );

  // Aspect picker coverage annotations — mirrors `buildPerspectiveCatalog`'s
  // in-scope/out-of-scope split, one row at a time: `ownerTypes` empty/absent
  // means "every type" (no annotation); otherwise flag when the aspect's
  // owning types don't fully cover this view's scope. Empty scope means
  // "unknown" — nothing is annotated, same as `buildPerspectiveCatalog`.
  const aspectAnnotations = useMemo(() => {
    if (aspectScopeTypeSystemNames.length === 0) return undefined;
    const scopeSet = new Set(aspectScopeTypeSystemNames);
    const annotations: Record<string, string> = {};
    propertyOptions.forEach((option) => {
      if (!option.ownerTypes || option.ownerTypes.length === 0) return;
      const covered = option.ownerTypes.filter((owner) => scopeSet.has(owner));
      if (covered.length === 0) {
        annotations[option.value] = t('context.viewEditor.atlas.aspectPicker.outOfScopeWarning');
      } else if (covered.length < aspectScopeTypeSystemNames.length) {
        annotations[option.value] = t('context.viewEditor.atlas.aspectPicker.partialCoverageWarning');
      }
    });
    return annotations;
  }, [propertyOptions, aspectScopeTypeSystemNames, t]);

  // `AspectCatalogMolecule` (shared with the filter editor's advanced rail) keys its
  // catalog by `key`, not `value` — this editor's `PropertyOption` predates that shared
  // shape. Adapted here rather than renaming `value` everywhere it's used (the Card C
  // "Visible properties" multiselect and friends). The coverage annotations ride each
  // row as its warning line, the same text the bound-aspect hint shows.
  const aspectPickerEntries = useMemo(
    () =>
      propertyOptionsToCatalogEntries(
        propertyOptions.map(({ value, ...option }) => ({
          ...option,
          key: value,
          type: option.type ?? 'text',
        })) as CatalogPropertyOption[],
        t,
        { annotations: aspectAnnotations }
      ),
    [propertyOptions, aspectAnnotations, t]
  );

  // --- View config handler ---

  const updateViewConfig = useCallback(
    (patch: Partial<ViewConfigFormState>) => {
      onChange({ ...value, viewConfig: { ...value.viewConfig, ...patch } as ViewConfigFormState });
    },
    [value, onChange]
  );

  // --- Atlas column handlers ---

  const addAtlasColumn = useCallback((molType: AtlasMoleculeType) => {
    if (value.viewConfig.type !== 'atlas') return;
    const meta = ATLAS_MOLECULE_CATALOG[molType];
    const defaultMoleculeProps = Object.fromEntries(meta.props.map((p) => [p.key, p.defaultValue]));
    const newCol: AtlasColumnFormState = { type: molType, moleculeProps: defaultMoleculeProps };
    const newColumns = [...(value.viewConfig.nodeMolecules ?? []), newCol];
    onChange({ ...value, viewConfig: { ...value.viewConfig, nodeMolecules: newColumns } });
    setSelectedAtlasColIdx(newColumns.length - 1);
  }, [value, onChange]);

  const updateAtlasColumn = useCallback(
    (index: number, patch: Partial<AtlasColumnFormState>) => {
      if (value.viewConfig.type !== 'atlas') return;
      const columns = (value.viewConfig.nodeMolecules ?? []).map((col, i) =>
        i === index ? carryAtlasColumnKey(col, { ...col, ...patch }) : col
      );
      onChange({ ...value, viewConfig: { ...value.viewConfig, nodeMolecules: columns } });
    },
    [value, onChange]
  );

  // Logs a reorder without committing one — the keyboard lift has already committed its
  // steps by the time it ends, so `onLiftEnd` needs the event and nothing else. `to` is
  // where the moved column now sits, so it names the right molecule type.
  const logAtlasColumnReorder = useCallback(
    (from: number, to: number) => {
      if (value.viewConfig.type !== 'atlas') return;
      const moved = (value.viewConfig.nodeMolecules ?? [])[to];
      if (!moved) return;
      logAnalyticsEvent('atlas_column_reordered', undefined, {
        moleculeType: moved.type,
        from,
        to,
        method: 'keyboard',
      });
    },
    [value, logAnalyticsEvent]
  );

  const removeAtlasColumn = useCallback(
    (index: number) => {
      if (value.viewConfig.type !== 'atlas') return;
      const target = (value.viewConfig.nodeMolecules ?? [])[index];
      // Locked (NodeLabel) and fixed-element (NodeCounts/NodePerspectivePill) rows are
      // never removable from the UI — guarded here too so a direct caller can't bypass
      // the disabled button (EAINT-857 Phase 11, mirrors `moveAtlasColumn`'s guard).
      if (target && (ATLAS_MOLECULE_CATALOG[target.type]?.locked || ATLAS_MOLECULE_CATALOG[target.type]?.fixedElement)) return;
      const columns = (value.viewConfig.nodeMolecules ?? []).filter((_, i) => i !== index);
      onChange({ ...value, viewConfig: { ...value.viewConfig, nodeMolecules: columns } });
      setSelectedAtlasColIdx((prev) => {
        if (prev === null) return null;
        if (prev >= columns.length) return Math.max(0, columns.length - 1);
        return prev;
      });
    },
    [value, onChange]
  );

  // A move is illegal if the source row is locked (NodeLabel) or a fixed element
  // (NodeCounts / NodePerspectivePill, which render from view-level flags, not
  // molecule position — EAINT-857 Phase 11), or if it would hop the item over a
  // locked/fixed row anywhere between its current and target position. Mirrors the
  // wireframe's `canMove()`. Shared by the arrow buttons, drag-and-drop and the
  // keyboard lift so none of the three input methods can bypass the guard the
  // others enforce.
  const canMoveAtlasColumn = useCallback(
    (from: number, to: number): boolean => {
      if (value.viewConfig.type !== 'atlas') return false;
      const cols = value.viewConfig.nodeMolecules ?? [];
      if (to < 0 || to >= cols.length || from < 0 || from >= cols.length || from === to) return false;
      const isBlocked = (col: AtlasColumnFormState) =>
        Boolean(ATLAS_MOLECULE_CATALOG[col.type]?.locked || ATLAS_MOLECULE_CATALOG[col.type]?.fixedElement);
      if (isBlocked(cols[from])) return false;
      const lo = Math.min(from, to);
      const hi = Math.max(from, to);
      for (let i = lo; i <= hi; i++) {
        if (i !== from && isBlocked(cols[i])) return false;
      }
      return true;
    },
    [value]
  );

  // `method: null` commits the move WITHOUT logging. A keyboard lift commits on every
  // Arrow step, so logging per commit would count one reorder intent as N events and a
  // cancelled lift as several with no net change; the keyboard path logs once from
  // `onLiftEnd` instead. Drag and the arrow buttons are one commit per intent already.
  const reorderAtlasColumn = useCallback(
    (from: number, to: number, method: 'drag' | 'keyboard' | 'arrow' | null) => {
      if (value.viewConfig.type !== 'atlas') return;
      if (!canMoveAtlasColumn(from, to)) return;
      const cols = [...(value.viewConfig.nodeMolecules ?? [])];
      const [moved] = cols.splice(from, 1);
      cols.splice(to, 0, moved);
      onChange({ ...value, viewConfig: { ...value.viewConfig, nodeMolecules: cols } });
      setSelectedAtlasColIdx(to);
      if (method) {
        logAnalyticsEvent('atlas_column_reordered', undefined, { moleculeType: moved.type, from, to, method });
      }
    },
    [value, onChange, canMoveAtlasColumn, logAnalyticsEvent]
  );

  const moveAtlasColumn = useCallback(
    (index: number, direction: -1 | 1) => {
      reorderAtlasColumn(index, index + direction, 'arrow');
    },
    [reorderAtlasColumn]
  );

  // --- Table column handlers ---

  const addTableColumn = useCallback(() => {
    if (value.viewConfig.type !== 'table') return;
    const newCol: TableColumnFormState = { property: '', header: { default: '' }, renderer: 'text' };
    onChange({
      ...value,
      viewConfig: { ...value.viewConfig, columns: [...value.viewConfig.columns, newCol] },
    });
  }, [value, onChange]);

  const updateTableColumn = useCallback(
    (index: number, patch: Partial<TableColumnFormState>) => {
      if (value.viewConfig.type !== 'table') return;
      const columns = value.viewConfig.columns.map((col, i) =>
        i === index ? { ...col, ...patch } : col
      );
      onChange({ ...value, viewConfig: { ...value.viewConfig, columns } });
    },
    [value, onChange]
  );

  const removeTableColumn = useCallback(
    (index: number) => {
      if (value.viewConfig.type !== 'table') return;
      const columns = value.viewConfig.columns.filter((_, i) => i !== index);
      onChange({ ...value, viewConfig: { ...value.viewConfig, columns } });
    },
    [value, onChange]
  );

  const viewTypeLabel = VIEW_TYPE_OPTIONS.find((o) => o.value === value.viewType)?.label ?? value.viewType;

  // ============================================================================
  // View-specific config bodies
  // ============================================================================

  const renderTreeMapConfig = () => {
    if (value.viewConfig.type !== 'treemap') return null;
    const config = value.viewConfig;

    return (
      <FieldRow
        label={t('context.viewEditor.treemap.columnWidthLabel')}
        description={t('context.viewEditor.treemap.columnWidthDesc')}
      >
        <div className="context-view-editor__inline">
          <InputNumberAtom
            value={config.columnWidth ?? 300}
            onChange={(val) => updateViewConfig({ columnWidth: val ?? 300 })}
            disabled={disabled}
            min={100}
            max={800}
            type="integer"
            className="context-view-editor__num"
            ariaLabel={t('context.viewEditor.treemap.columnWidthLabel')}
          />
          <span className="context-view-editor__unit">{t('context.viewEditor.treemap.px')}</span>
        </div>
      </FieldRow>
    );
  };

  /**
   * Keyed lookup rather than a switch: the vocabulary now has ten members and a switch
   * that missed one returned `undefined`, which rendered as a blank column name. The
   * key mirrors the type exactly, so adding a type to the union and to the locale bundle
   * is all that a new molecule needs.
   */
  const getMoleculeLabel = (type: AtlasMoleculeType): string =>
    t(`context.viewEditor.atlas.molecule.${type}`, { defaultValue: type });

  const getMoleculeDesc = (type: AtlasMoleculeType): string =>
    t(`context.viewEditor.atlas.molecule.${type}Desc`, { defaultValue: '' });

  const renderAtlasConfig = () => {
    if (value.viewConfig.type !== 'atlas') return null;
    // `columns` is this editor's UI vocabulary for the row; `nodeMolecules` is what is
    // stored and rendered. Aliased once here so the render code below reads naturally.
    const config = { ...value.viewConfig, columns: value.viewConfig.nodeMolecules ?? [] };

    const selectedCol =
      selectedAtlasColIdx !== null && selectedAtlasColIdx < config.columns.length
        ? config.columns[selectedAtlasColIdx]
        : null;

    const availableMoleculeTypes = (Object.keys(ATLAS_MOLECULE_CATALOG) as AtlasMoleculeType[]).filter(
      (type) => ATLAS_MOLECULE_CATALOG[type].addable
    );

    return (
      <div className="context-view-editor__atlas-config">

        {/*
          Description sits at the top of the card, above the preview — not in the header.
          It was briefly placed in the header bar because collapsing to one card left it
          without a home, but the hand-off's own `AVEditorBar` shows no Description at all,
          so the header could not arbitrate that placement. It belongs with the thing it
          describes.
        */}
        <div className="context-view-editor__atlas-description">
          <span className="context-view-editor__atlas-section-label">
            {t('context.viewEditor.cover.description')}
          </span>
          <TextareaAtom
            id="context-view-editor-atlas-description"
            className="context-view-editor__atlas-description-textarea"
            value={value.description?.default ?? ''}
            onChange={(e) =>
              onChange({
                ...value,
                description: { ...value.description, default: e.target.value },
              })
            }
            placeholder={t('context.viewEditor.cover.descriptionPlaceholder')}
            disabled={disabled}
            rows={2}
            aria-label={t('context.viewEditor.cover.description')}
          />
        </div>

        {/* Live preview */}
        <span className="context-view-editor__atlas-section-label context-view-editor__atlas-section-label--preview">
          {t('context.viewEditor.atlas.livePreview')}
        </span>
        {/*
          Renders the REAL Atlas row renderer (`AtlasLivePreviewMolecule`) rather than a
          hand-built mock: `Node` is driven by the draft config off `AtlasViewContext`, so
          built-in toggles, ordering and per-type rendering are truthful for free. The
          molecule owns its own empty state (zero columns) and frame styling.

          CAVEAT: mounting `atlas-header` also fires `useAvailableTypes(apiClients,
          featuredSphereId)`. That feeds only the sphere-select dropdown (L945). When
          `rootSphereId` is supplied (threaded from `AtlasComponent` via the overlay,
          EAINT-857 Phase 4) the preview uses the real sphere, so this is a legitimate
          request; without it (e.g. an editor rendered outside AtlasComponent) it falls
          back to the fabricated preview id and the fetch simply resolves to nothing.
        */}
        {/*
          Built explicitly rather than spread: `AtlasConfigFormState` makes
          `nodeMolecules` optional where the canonical `AtlasViewConfig` requires it.
          Naming the fields keeps the preview honest about what Atlas actually reads —
          a flag omitted here is a flag the preview would silently lie about.
        */}
        <AtlasLivePreviewMolecule
          viewConfig={{
            type: 'atlas',
            nodeMolecules: config.columns,
            showTraversalArrows: config.showTraversalArrows,
            allowTraversalDirectionChange: config.allowTraversalDirectionChange,
            showNearbyNodeCount: config.showNearbyNodeCount,
            showUrgency: config.showUrgency,
            lockFocus: config.lockFocus,
            hideAddSphere: config.hideAddSphere,
            mobileBreakpoint: config.mobileBreakpoint,
          }}
          rootSphereId={rootSphereId}
          selectedColumnIndex={selectedAtlasColIdx}
          onColumnSelect={setSelectedAtlasColIdx}
        />

        <div className="context-view-editor__atlas-divider" />

        {/* Built-in elements */}
        <div className="context-view-editor__atlas-section">
          <span className="context-view-editor__atlas-section-label">
            {t('context.viewEditor.atlas.builtIn.title')}
          </span>
          <div className="context-view-editor__atlas-built-in-grid">
            {/* Status — always shown, locked */}
            <div className="context-view-editor__atlas-built-in-item">
              <div className="context-view-editor__atlas-built-in-icon">
                <i className="pi pi-circle-fill" />
              </div>
              <div className="context-view-editor__atlas-built-in-info">
                <span className="context-view-editor__atlas-built-in-name">{t('context.viewEditor.atlas.builtIn.status')}</span>
                <span className="context-view-editor__atlas-built-in-desc">{t('context.viewEditor.atlas.builtIn.statusDesc')}</span>
              </div>
              <span className="context-view-editor__atlas-built-in-lock">
                <i className="pi pi-lock" aria-hidden="true" />
                <span className="context-view-editor__sr-only">{t('context.viewEditor.atlas.alwaysBadge')}</span>
              </span>
            </div>

            {/* Urgency */}
            <div className="context-view-editor__atlas-built-in-item">
              <div className="context-view-editor__atlas-built-in-icon">
                <i className="pi pi-exclamation-triangle" />
              </div>
              <div className="context-view-editor__atlas-built-in-info">
                <span className="context-view-editor__atlas-built-in-name">{t('context.viewEditor.atlas.builtIn.urgency')}</span>
                <span className="context-view-editor__atlas-built-in-desc">{t('context.viewEditor.atlas.builtIn.urgencyDesc')}</span>
              </div>
              <label className="context-view-editor__toggle-item context-view-editor__toggle-item--inline">
                <input
                  type="checkbox"
                  className="context-view-editor__toggle-input"
                  checked={config.showUrgency !== false}
                  onChange={(e) => updateViewConfig({ showUrgency: e.target.checked })}
                  disabled={disabled}
                  aria-label={t('context.viewEditor.atlas.builtIn.urgency')}
                />
                <span className="context-view-editor__toggle-switch" />
              </label>
            </div>

            {/* Connections */}
            <div className="context-view-editor__atlas-built-in-item">
              <div className="context-view-editor__atlas-built-in-icon">
                <i className="pi pi-share-alt" />
              </div>
              <div className="context-view-editor__atlas-built-in-info">
                <span className="context-view-editor__atlas-built-in-name">{t('context.viewEditor.atlas.builtIn.connections')}</span>
                <span className="context-view-editor__atlas-built-in-desc">{t('context.viewEditor.atlas.builtIn.connectionsDesc')}</span>
              </div>
              <label className="context-view-editor__toggle-item context-view-editor__toggle-item--inline">
                <input
                  type="checkbox"
                  className="context-view-editor__toggle-input"
                  checked={config.showNearbyNodeCount !== false}
                  onChange={(e) => updateViewConfig({ showNearbyNodeCount: e.target.checked })}
                  disabled={disabled}
                  aria-label={t('context.viewEditor.atlas.builtIn.connections')}
                />
                <span className="context-view-editor__toggle-switch" />
              </label>
            </div>

            {/* Explore */}
            <div className="context-view-editor__atlas-built-in-item">
              <div className="context-view-editor__atlas-built-in-icon">
                <i className="pi pi-compass" />
              </div>
              <div className="context-view-editor__atlas-built-in-info">
                <span className="context-view-editor__atlas-built-in-name">{t('context.viewEditor.atlas.builtIn.explore')}</span>
                <span className="context-view-editor__atlas-built-in-desc">{t('context.viewEditor.atlas.builtIn.exploreDesc')}</span>
              </div>
              <label className="context-view-editor__toggle-item context-view-editor__toggle-item--inline">
                <input
                  type="checkbox"
                  className="context-view-editor__toggle-input"
                  checked={config.showTraversalArrows === true}
                  onChange={(e) => updateViewConfig({ showTraversalArrows: e.target.checked })}
                  disabled={disabled}
                  aria-label={t('context.viewEditor.atlas.builtIn.explore')}
                />
                <span className="context-view-editor__toggle-switch" />
              </label>
            </div>
          </div>
        </div>

        <div className="context-view-editor__atlas-divider" />

        {/* Columns in order + Inspector */}
        <div className="context-view-editor__atlas-section">
          <span className="context-view-editor__atlas-section-label">
            {t('context.viewEditor.atlas.columnsInOrder')}
          </span>
          <div className="context-view-editor__atlas-col-panel">

            {/* Left: ordered list */}
            <div className="context-view-editor__atlas-col-list">
              <ReorderableListMolecule<AtlasColumnFormState>
                items={config.columns}
                getKey={atlasColumnKey}
                disabled={disabled}
                canMove={canMoveAtlasColumn}
                onReorder={(from, to, source) =>
                  // A keyboard step is intermediate: it commits so the list animates, but
                  // the lift is counted once, from `onLiftEnd`.
                  reorderAtlasColumn(from, to, source === 'keyboard' ? null : 'drag')
                }
                onLiftEnd={logAtlasColumnReorder}
                isItemDraggable={(col) =>
                  !(ATLAS_MOLECULE_CATALOG[col.type]?.locked || ATLAS_MOLECULE_CATALOG[col.type]?.fixedElement)
                }
                getRowClassName={(_col, i) =>
                  `context-view-editor__atlas-col-row${selectedAtlasColIdx === i ? ' context-view-editor__atlas-col-row--selected' : ''}`
                }
                labels={{
                  listLabel: t('context.viewEditor.atlas.columnsInOrder'),
                  rowLabel: (i, total) => t('context.viewEditor.atlas.reorderRowLabel', { position: i + 1, total }),
                  liftedAnnouncement: (i, total) => t('context.viewEditor.atlas.reorderLifted', { position: i + 1, total }),
                  movedAnnouncement: (i, total) => t('context.viewEditor.atlas.reorderMoved', { position: i + 1, total }),
                  droppedAnnouncement: (i, total) => t('context.viewEditor.atlas.reorderDropped', { position: i + 1, total }),
                  cancelledAnnouncement: t('context.viewEditor.atlas.reorderCancelled'),
                }}
                renderItem={(col, i) => {
                  const meta = ATLAS_MOLECULE_CATALOG[col.type];
                  const isLocked = meta?.locked;
                  // Fixed elements (NodeCounts / NodePerspectivePill) render from view-level
                  // flags now, not from `nodeMolecules` position (spec Phase 2) — reordering
                  // or removing a still-present legacy entry has zero rendering effect, so
                  // it is immovable/unremovable exactly like a locked column (EAINT-857
                  // Phase 11: previously these stayed interactive to no effect).
                  const isImmovable = isLocked || meta?.fixedElement;
                  const isSelected = selectedAtlasColIdx === i;
                  const prevBlocks = i > 0 && !canMoveAtlasColumn(i, i - 1);
                  const nextBlocks = i < config.columns.length - 1 && !canMoveAtlasColumn(i, i + 1);
                  return (
                    // A presentational <>, not an interactive wrapper: the row (owned by
                    // ReorderableListMolecule) holds the move-up/move-down <button>s AND
                    // the select control, and neither a nested <button> (invalid HTML) nor
                    // a role="button" with focusable descendants (invalid ARIA) is allowed.
                    // The select control is therefore its own <button> sibling covering the
                    // glyph + label + badge. Arrows are retained alongside drag/keyboard
                    // reordering — the hand-off itself uses chevrons.
                    <>
                      {!isImmovable && (
                        <div className="context-view-editor__atlas-move-btns" role="group" aria-label={t('context.viewEditor.atlas.moveColumn')}>
                          <button
                            type="button"
                            className="context-view-editor__atlas-move-btn"
                            onClick={(e) => { e.stopPropagation(); moveAtlasColumn(i, -1); }}
                            disabled={disabled || i === 0 || prevBlocks}
                            aria-label={t('context.viewEditor.atlas.moveUp')}
                          >
                            <i className="pi pi-chevron-up" />
                          </button>
                          <button
                            type="button"
                            className="context-view-editor__atlas-move-btn"
                            onClick={(e) => { e.stopPropagation(); moveAtlasColumn(i, 1); }}
                            disabled={disabled || i >= config.columns.length - 1 || nextBlocks}
                            aria-label={t('context.viewEditor.atlas.moveDown')}
                          >
                            <i className="pi pi-chevron-down" />
                          </button>
                        </div>
                      )}
                      {isImmovable && <div className="context-view-editor__atlas-move-btns-spacer" />}
                      <button
                        type="button"
                        className="context-view-editor__atlas-col-select"
                        onClick={() => setSelectedAtlasColIdx(i)}
                        disabled={disabled}
                        aria-pressed={isSelected}
                      >
                        <span className="context-view-editor__atlas-col-glyph">
                          {meta?.glyph
                            ? <span className="context-view-editor__atlas-col-glyph-text">{meta.glyph}</span>
                            : <i className={meta?.icon ?? 'pi pi-circle'} />}
                        </span>
                        <span className="context-view-editor__atlas-col-label">
                          {getMoleculeLabel(col.type)}
                        </span>
                        {/*
                          At most one badge, and `always` wins: a locked column cannot be
                          dropped when space is tight, so showing `keep` beside it would
                          state a weaker guarantee than the one already in force.
                        */}
                        {isLocked && (
                          <span className="context-view-editor__atlas-col-badge context-view-editor__atlas-col-badge--always">
                            <i className="pi pi-lock" />
                            {t('context.viewEditor.atlas.alwaysBadge')}
                          </span>
                        )}
                        {!isLocked && Boolean(col.moleculeProps?.important) && (
                          <span className="context-view-editor__atlas-col-badge context-view-editor__atlas-col-badge--keep">
                            <i className="pi pi-thumbtack" />
                            {t('context.viewEditor.atlas.keepBadge')}
                          </span>
                        )}
                      </button>
                    </>
                  );
                }}
              />

              {/* Add column */}
              <div
                className="context-view-editor__atlas-add-wrap"
                onKeyDown={(e) => {
                  if (e.key === 'Escape' && addingAtlasColumn) {
                    e.stopPropagation();
                    closeAtlasAddPopover();
                  }
                }}
              >
                <button
                  ref={atlasAddTriggerRef}
                  type="button"
                  className="context-view-editor__atlas-add-btn"
                  onClick={() => setAddingAtlasColumn((v) => !v)}
                  disabled={disabled}
                >
                  <i className="pi pi-plus" />
                  {t('context.viewEditor.atlas.addColumn')}
                </button>
                {addingAtlasColumn && (
                  <>
                    {/* Pointer-only dismissal surface: conveys nothing to assistive
                        tech and is not focusable, so it is hidden from the a11y
                        tree. Keyboard users dismiss with Escape (see the wrapper's
                        onKeyDown), which is the documented listbox behaviour. */}
                    <div
                      className="context-view-editor__atlas-add-backdrop"
                      aria-hidden="true"
                      onClick={closeAtlasAddPopover}
                    />
                    <div
                    className="context-view-editor__atlas-add-popover"
                    role="listbox"
                    aria-label={t('context.viewEditor.atlas.addColumn')}
                  >
                      {availableMoleculeTypes.map((type) => {
                        // Single-instance types (ArcStageSelector, ApproachBadges) can only
                        // ever have one column on the view — once one exists, the option is
                        // disabled and marked "Added" rather than silently permitting a
                        // second column that would draw identically to the first.
                        const singleInstance = Boolean(ATLAS_MOLECULE_CATALOG[type].singleInstance);
                        const addedCount = config.columns.filter((col) => col.type === type).length;
                        const alreadyAdded = singleInstance && addedCount > 0;
                        // Repeatability is stated on EVERY row, not only on the disabled
                        // ones: a single greyed "Added" example teaches "each type can be
                        // added once", which is wrong for Sphere aspect — one column per
                        // aspect is the entire point of that type.
                        // Two independent booleans -> four copy variants. A keyed lookup
                        // states the matrix directly; nesting ternaries hid which of the
                        // four a reader was looking at.
                        let repeatabilityLabel: string;
                        if (singleInstance) {
                          repeatabilityLabel =
                            addedCount > 0
                              ? t('context.viewEditor.atlas.repeatability.oneOnlyAdded')
                              : t('context.viewEditor.atlas.repeatability.oneOnly');
                        } else {
                          repeatabilityLabel =
                            addedCount > 0
                              ? t('context.viewEditor.atlas.repeatability.addAnother', { count: addedCount })
                              : t('context.viewEditor.atlas.repeatability.addMany');
                        }
                        return (
                          <button
                            key={type}
                            type="button"
                            role="option"
                            aria-selected={false}
                            aria-disabled={alreadyAdded}
                            disabled={alreadyAdded}
                            className={`context-view-editor__atlas-add-option${alreadyAdded ? ' context-view-editor__atlas-add-option--added' : ''}`}
                            onClick={() => {
                              if (alreadyAdded) return;
                              addAtlasColumn(type);
                              closeAtlasAddPopover();
                            }}
                          >
                            <div className="context-view-editor__atlas-col-glyph context-view-editor__atlas-col-glyph--sm" data-type={type}>
                              {ATLAS_MOLECULE_CATALOG[type].glyph
                                ? <span className="context-view-editor__atlas-col-glyph-text">{ATLAS_MOLECULE_CATALOG[type].glyph}</span>
                                : <i className={ATLAS_MOLECULE_CATALOG[type].icon} />}
                            </div>
                            <div>
                              <div className="context-view-editor__atlas-add-option-label">{getMoleculeLabel(type)}</div>
                              <div className="context-view-editor__atlas-add-option-desc">{getMoleculeDesc(type)}</div>
                            </div>
                            <span
                              className={`context-view-editor__atlas-col-badge context-view-editor__atlas-add-option-repeatability${
                                alreadyAdded
                                  ? ' context-view-editor__atlas-col-badge--always'
                                  : singleInstance
                                  ? ' context-view-editor__atlas-add-option-repeatability--one-only'
                                  : ' context-view-editor__atlas-add-option-repeatability--unlimited'
                              }`}
                            >
                              {repeatabilityLabel}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Right: inspector */}
            <div className="context-view-editor__atlas-inspector">
              {selectedCol === null ? (
                <div className="context-view-editor__atlas-inspector-empty">
                  <i className="pi pi-mouse-pointer" />
                  <span>{t('context.viewEditor.atlas.selectColumn')}</span>
                  <span className="context-view-editor__atlas-inspector-empty-hint">
                    {t('context.viewEditor.atlas.selectColumnDesc')}
                  </span>
                </div>
              ) : (
                <div className="context-view-editor__atlas-inspector-card">
                  <div className="context-view-editor__atlas-inspector-head">
                    <div className="context-view-editor__atlas-col-glyph">
                      {ATLAS_MOLECULE_CATALOG[selectedCol.type]?.glyph
                        ? <span className="context-view-editor__atlas-col-glyph-text">{ATLAS_MOLECULE_CATALOG[selectedCol.type].glyph}</span>
                        : <i className={ATLAS_MOLECULE_CATALOG[selectedCol.type]?.icon ?? 'pi pi-circle'} />}
                    </div>
                    <div className="context-view-editor__atlas-inspector-title-group">
                      <span className="context-view-editor__atlas-inspector-name">
                        {getMoleculeLabel(selectedCol.type)}
                      </span>
                    </div>
                    {!ATLAS_MOLECULE_CATALOG[selectedCol.type]?.locked &&
                      !ATLAS_MOLECULE_CATALOG[selectedCol.type]?.fixedElement && (
                      <button
                        type="button"
                        className="context-view-editor__atlas-inspector-remove"
                        onClick={() => removeAtlasColumn(selectedAtlasColIdx!)}
                        disabled={disabled}
                        aria-label={t('context.viewEditor.atlas.removeColumnAriaLabel', { index: (selectedAtlasColIdx ?? 0) + 1 })}
                      >
                        <i className="pi pi-trash" />
                      </button>
                    )}
                  </div>

                  <p className="context-view-editor__atlas-inspector-desc">
                    {getMoleculeDesc(selectedCol.type)}
                  </p>

                  {/* Molecule props */}
                  {ATLAS_MOLECULE_CATALOG[selectedCol.type]?.props.map((prop) => {
                    const rawValue = selectedCol.moleculeProps?.[prop.key];

                    if (prop.type === 'number') {
                      return (
                        <div key={prop.key} className="context-view-editor__atlas-inspector-field">
                          <span className="context-view-editor__atlas-inspector-field-label">{t(prop.i18nLabel)}</span>
                          <InputNumberAtom
                            value={(rawValue as number) ?? (prop.defaultValue as number)}
                            onChange={(val) =>
                              updateAtlasColumn(selectedAtlasColIdx!, {
                                moleculeProps: { ...selectedCol.moleculeProps, [prop.key]: val ?? prop.defaultValue },
                              })
                            }
                            disabled={disabled}
                            ariaLabel={t(prop.i18nLabel)}
                            min={prop.min}
                            max={prop.max}
                            type="integer"
                            className="context-view-editor__num"
                          />
                        </div>
                      );
                    }

                    if (prop.type === 'segmented') {
                      const segmentedValue = (rawValue as string) ?? (prop.defaultValue as string);
                      return (
                        <div key={prop.key} className="context-view-editor__atlas-inspector-field">
                          <span className="context-view-editor__atlas-inspector-field-label">{t(prop.i18nLabel)}</span>
                          <SegmentedControlAtom
                            mode="single"
                            variant="pills"
                            size="small"
                            value={segmentedValue}
                            options={(prop.options ?? []).map((opt) => ({ value: opt.value, label: t(opt.i18nLabel) }))}
                            onChange={(val) =>
                              updateAtlasColumn(selectedAtlasColIdx!, {
                                moleculeProps: { ...selectedCol.moleculeProps, [prop.key]: val },
                              })
                            }
                            ariaLabel={t(prop.i18nLabel)}
                          />
                        </div>
                      );
                    }

                    if (prop.type === 'toggle') {
                      const toggleValue = (rawValue as boolean) ?? (prop.defaultValue as boolean);
                      return (
                        <div key={prop.key} className="context-view-editor__atlas-inspector-field context-view-editor__atlas-inspector-field--toggle">
                          <span className="context-view-editor__atlas-inspector-field-label">{t(prop.i18nLabel)}</span>
                          <label className="context-view-editor__toggle-item context-view-editor__toggle-item--inline">
                            <input
                              type="checkbox"
                              className="context-view-editor__toggle-input"
                              checked={toggleValue}
                              onChange={(e) =>
                                updateAtlasColumn(selectedAtlasColIdx!, {
                                  moleculeProps: { ...selectedCol.moleculeProps, [prop.key]: e.target.checked },
                                })
                              }
                              disabled={disabled}
                              aria-label={t(prop.i18nLabel)}
                            />
                            <span className="context-view-editor__toggle-switch" />
                          </label>
                        </div>
                      );
                    }

                    if (prop.type === 'perspective') {
                      const boundSystemName = (rawValue as string) || '';
                      const boundGroup = boundSystemName
                        ? perspectiveCatalog.find((g) => g.perspectives.some((p) => p.systemName === boundSystemName))
                        : undefined;
                      const boundLabel = boundSystemName
                        ? boundGroup?.perspectives.find((p) => p.systemName === boundSystemName)?.label ?? boundSystemName
                        : t('context.viewEditor.atlas.perspectivePicker.triggerPlaceholder');
                      // Mirrors the aspect field's stale hint: a picker-list OUT OF SCOPE
                      // badge (PerspectivePickerMolecule) only warns while browsing — once
                      // bound, the trigger itself must say why every cell will be empty.
                      const isOutOfScope = Boolean(boundGroup) && !boundGroup!.inScope;
                      return (
                        <div key={prop.key} className="context-view-editor__atlas-inspector-field">
                          <span className="context-view-editor__atlas-inspector-field-label">{t(prop.i18nLabel)}</span>
                          <div className="context-view-editor__perspective-picker-wrap">
                            <button
                              ref={perspectivePickerTriggerRef}
                              type="button"
                              className="context-view-editor__perspective-picker-trigger"
                              onClick={(e) => perspectivePickerOverlayRef.current?.toggle(e)}
                              disabled={disabled}
                            >
                              <span>{boundLabel}</span>
                              <i className="pi pi-chevron-down" aria-hidden="true" />
                            </button>
                            {isOutOfScope && (
                              <span className="context-view-editor__perspective-out-of-scope-hint">
                                {t('context.viewEditor.atlas.perspectivePicker.outOfScopeWarning')}
                              </span>
                            )}
                            <OverlayPanelAtom
                              ref={perspectivePickerOverlayRef}
                              className="context-view-editor__perspective-picker-popover"
                              dismissable
                              onHide={() => perspectivePickerTriggerRef.current?.focus()}
                            >
                                  <PerspectivePickerMolecule
                                    current={boundSystemName || null}
                                    groups={perspectiveCatalog}
                                    onPick={(systemName) => {
                                      updateAtlasColumn(selectedAtlasColIdx!, {
                                        moleculeProps: { ...selectedCol.moleculeProps, [prop.key]: systemName },
                                      });
                                      closePerspectivePicker();
                                    }}
                                    testIdPrefix="atlas-perspective-picker-option"
                                  />
                            </OverlayPanelAtom>
                          </div>
                        </div>
                      );
                    }

                    if (prop.type === 'aspect') {
                      const boundKey = (rawValue as string) || '';
                      const boundOption = boundKey
                        ? propertyOptions.find((o) => o.value === boundKey)
                        : undefined;
                      // Stale: a key is stored but no catalog entry matches it, and the
                      // catalog has actually finished loading — never render a blank
                      // trigger for this, the failure mode the picker exists to prevent.
                      const isStale = Boolean(boundKey) && !boundOption && !propertyOptionsLoading;

                      const pickAspect = (key: string) => {
                        const picked = propertyOptions.find((o) => o.value === key);
                        const nextMoleculeProps: Record<string, unknown> = {
                          ...selectedCol.moleculeProps,
                          [prop.key]: key,
                        };
                        // Switching kinds clears the props the new kind doesn't have —
                        // otherwise a stale `isFriendly`/`choiceStyle` from the previous
                        // aspect silently rides along with no control left to change it.
                        if (!isDateAspectKind(picked?.type)) delete nextMoleculeProps.isFriendly;
                        if (!isChoiceAspectKind(picked?.type)) {
                          delete nextMoleculeProps.choiceStyle;
                          delete nextMoleculeProps.maxValues;
                          delete nextMoleculeProps.overflow;
                        }
                        if (!isReferenceAspectKind(picked?.type)) delete nextMoleculeProps.showAvatar;
                        if (!isNumberAspectKind(picked?.type)) delete nextMoleculeProps.numberFormat;
                        if (!isTextAspectKind(picked?.type)) delete nextMoleculeProps.characterLimit;
                        updateAtlasColumn(selectedAtlasColIdx!, { moleculeProps: nextMoleculeProps });
                        closeAspectPicker();
                      };

                      return (
                        <div key={prop.key} className="context-view-editor__atlas-inspector-field">
                          <span className="context-view-editor__atlas-inspector-field-label">{t(prop.i18nLabel)}</span>
                          <div className="context-view-editor__aspect-picker-wrap">
                            {isStale ? (
                              <button
                                ref={aspectPickerTriggerRef}
                                type="button"
                                className="context-view-editor__aspect-picker-trigger context-view-editor__aspect-picker-trigger--stale"
                                onClick={(e) => aspectPickerOverlayRef.current?.toggle(e)}
                                disabled={disabled}
                              >
                                <ChipAtom
                                  label={boundKey}
                                  unavailable
                                  unavailableReason={t('context.viewEditor.atlas.aspectPicker.staleReason')}
                                />
                              </button>
                            ) : (
                              <button
                                ref={aspectPickerTriggerRef}
                                type="button"
                                className="context-view-editor__aspect-picker-trigger"
                                onClick={(e) => aspectPickerOverlayRef.current?.toggle(e)}
                                disabled={disabled}
                              >
                                <span>{boundOption?.label ?? t('context.viewEditor.atlas.aspectPicker.triggerPlaceholder')}</span>
                                {boundOption?.type && (
                                  <ChipAtom
                                    label={t(`filterEditor.panel.typeLabel.${boundOption.type}`)}
                                    icon={`pi ${getFilterTypeVisual(boundOption.type).icon}`}
                                    className="context-view-editor__aspect-kind-chip"
                                  />
                                )}
                                <i className="pi pi-chevron-down" aria-hidden="true" />
                              </button>
                            )}
                            {isStale && (
                              <span className="context-view-editor__aspect-stale-hint">
                                {t('context.viewEditor.atlas.aspectPicker.staleReason')}
                              </span>
                            )}
                            {/*
                              Coverage warning on the BOUND trigger, not only inside the
                              picker list: once an aspect is bound, the field itself has to
                              say why some (or every) cell will be empty — the same contract
                              the perspective field above honours.
                            */}
                            {!isStale && boundKey && aspectAnnotations?.[boundKey] && (
                              <span className="context-view-editor__aspect-coverage-hint">
                                {aspectAnnotations[boundKey]}
                              </span>
                            )}
                            <OverlayPanelAtom
                              ref={aspectPickerOverlayRef}
                              className="context-view-editor__aspect-picker-popover"
                              dismissable
                              onHide={() => aspectPickerTriggerRef.current?.focus()}
                            >
                                  <AspectCatalogMolecule
                                    selectedKey={boundKey || null}
                                    entries={aspectPickerEntries}
                                    onPick={(entry) => pickAspect(entry.key)}
                                    onCancel={closeAspectPicker}
                                    scopeTypeLabels={aspectScopeTypeSystemNames}
                                    loading={propertyOptionsLoading}
                                    error={propertyOptionsError}
                                    onRetry={onRetryPropertyOptions}
                                    testIdPrefix="atlas-aspect-picker-option"
                                  />
                            </OverlayPanelAtom>
                          </div>

                          {/* Per-kind options — only the ones AtlasSpherePropertyMolecule actually reads. */}
                          {boundOption && isDateAspectKind(boundOption.type) && (
                            <div className="context-view-editor__atlas-inspector-field context-view-editor__atlas-inspector-field--toggle">
                              <span className="context-view-editor__atlas-inspector-field-label">
                                {t('context.viewEditor.atlas.isFriendly')}
                              </span>
                              <label className="context-view-editor__toggle-item context-view-editor__toggle-item--inline">
                                <input
                                  type="checkbox"
                                  className="context-view-editor__toggle-input"
                                  checked={(selectedCol.moleculeProps?.isFriendly as boolean) ?? true}
                                  onChange={(e) =>
                                    updateAtlasColumn(selectedAtlasColIdx!, {
                                      moleculeProps: { ...selectedCol.moleculeProps, isFriendly: e.target.checked },
                                    })
                                  }
                                  disabled={disabled}
                                  aria-label={t('context.viewEditor.atlas.isFriendly')}
                                />
                                <span className="context-view-editor__toggle-switch" />
                              </label>
                            </div>
                          )}
                          {boundOption && isChoiceAspectKind(boundOption.type) && (
                            <div className="context-view-editor__atlas-inspector-field">
                              <span className="context-view-editor__atlas-inspector-field-label">
                                {t('context.viewEditor.atlas.aspectPicker.choiceStyle')}
                              </span>
                              <SegmentedControlAtom
                                mode="single"
                                variant="pills"
                                size="small"
                                value={(selectedCol.moleculeProps?.choiceStyle as string) ?? 'text'}
                                options={[
                                  { value: 'chip', label: t('context.viewEditor.atlas.aspectPicker.choiceStyleChip') },
                                  { value: 'text', label: t('context.viewEditor.atlas.aspectPicker.choiceStyleText') },
                                ]}
                                onChange={(val) =>
                                  updateAtlasColumn(selectedAtlasColIdx!, {
                                    moleculeProps: { ...selectedCol.moleculeProps, choiceStyle: val },
                                  })
                                }
                                ariaLabel={t('context.viewEditor.atlas.aspectPicker.choiceStyle')}
                              />
                            </div>
                          )}
                          {boundOption && isReferenceAspectKind(boundOption.type) && (
                            <div className="context-view-editor__atlas-inspector-field context-view-editor__atlas-inspector-field--toggle">
                              <span className="context-view-editor__atlas-inspector-field-label">
                                {t('context.viewEditor.atlas.aspectPicker.showAvatar')}
                              </span>
                              <label className="context-view-editor__toggle-item context-view-editor__toggle-item--inline">
                                <input
                                  type="checkbox"
                                  className="context-view-editor__toggle-input"
                                  checked={(selectedCol.moleculeProps?.showAvatar as boolean) ?? false}
                                  onChange={(e) =>
                                    updateAtlasColumn(selectedAtlasColIdx!, {
                                      moleculeProps: { ...selectedCol.moleculeProps, showAvatar: e.target.checked },
                                    })
                                  }
                                  disabled={disabled}
                                  aria-label={t('context.viewEditor.atlas.aspectPicker.showAvatar')}
                                />
                                <span className="context-view-editor__toggle-switch" />
                              </label>
                            </div>
                          )}
                          {boundOption && isNumberAspectKind(boundOption.type) && (
                            <div className="context-view-editor__atlas-inspector-field">
                              <span className="context-view-editor__atlas-inspector-field-label">
                                {t('context.viewEditor.atlas.aspectPicker.numberFormat')}
                              </span>
                              <SegmentedControlAtom
                                mode="single"
                                variant="pills"
                                size="small"
                                value={(selectedCol.moleculeProps?.numberFormat as string) ?? 'value'}
                                options={[
                                  { value: 'value', label: t('context.viewEditor.atlas.aspectPicker.numberFormatValue') },
                                  { value: 'bar', label: t('context.viewEditor.atlas.aspectPicker.numberFormatBar') },
                                  { value: 'percent', label: t('context.viewEditor.atlas.aspectPicker.numberFormatPercent') },
                                ]}
                                onChange={(val) =>
                                  updateAtlasColumn(selectedAtlasColIdx!, {
                                    moleculeProps: { ...selectedCol.moleculeProps, numberFormat: val },
                                  })
                                }
                                ariaLabel={t('context.viewEditor.atlas.aspectPicker.numberFormat')}
                              />
                            </div>
                          )}
                          {/* Where the text kind's cell truncates and offers click-to-expand.
                              Read by `AtlasSpherePropertyMolecule`'s text branch, so this control
                              closes the loop the collapse opened: the 21 migrated `AtlasTextColumn`
                              molecules carry a stored limit of 10 while a newly authored aspect takes
                              the renderer's default of 100, and without a control an author could see
                              the difference but not change it (EAINT-866 SC4). The default shown here
                              MUST match the renderer's `characterLimit = 100` default — a lower
                              placeholder would silently retruncate every unset column the first time
                              an author touched any other option on the same cell. */}
                          {boundOption && isTextAspectKind(boundOption.type) && (
                            <div className="context-view-editor__atlas-inspector-field">
                              <span className="context-view-editor__atlas-inspector-field-label">
                                {t('context.viewEditor.atlas.aspectPicker.characterLimit')}
                              </span>
                              <InputNumberAtom
                                value={(selectedCol.moleculeProps?.characterLimit as number) ?? 100}
                                onChange={(val) =>
                                  updateAtlasColumn(selectedAtlasColIdx!, {
                                    moleculeProps: { ...selectedCol.moleculeProps, characterLimit: val ?? 100 },
                                  })
                                }
                                disabled={disabled}
                                ariaLabel={t('context.viewEditor.atlas.aspectPicker.characterLimit')}
                                min={10}
                                max={200}
                                type="integer"
                                className="context-view-editor__num"
                              />
                            </div>
                          )}
                          {/* `AtlasSpherePropertyMolecule` only reads `maxValues` when the stored value is an
                              array (its top-level `Array.isArray(rawValue)` branch, ahead of any
                              kind-specific rendering) — no schema property today declares multiplicity
                              (there is no `metadata.multiple`/cardinality field, matching OQ-E's provenance
                              gap), so the editor cannot know in general whether a bound aspect will ever
                              hold an array. Choice aspects are the one kind with an established multi-value
                              convention in this codebase (`metadata.options`-backed multi-select); date,
                              reference and number aspects are single-valued by every convention this schema
                              model has, so the control no longer offers itself for those (EAINT-857 Phase 11 —
                              previously rendered, and had no effect, for every kind). */}
                          {boundOption && isChoiceAspectKind(boundOption.type) && (
                            <div className="context-view-editor__atlas-inspector-field">
                              <span className="context-view-editor__atlas-inspector-field-label">
                                {t('context.viewEditor.atlas.aspectPicker.maxValues')}
                              </span>
                              <InputNumberAtom
                                value={(selectedCol.moleculeProps?.maxValues as number) ?? 2}
                                onChange={(val) =>
                                  updateAtlasColumn(selectedAtlasColIdx!, {
                                    moleculeProps: { ...selectedCol.moleculeProps, maxValues: val ?? 2 },
                                  })
                                }
                                disabled={disabled}
                                ariaLabel={t('context.viewEditor.atlas.aspectPicker.maxValues')}
                                min={1}
                                max={10}
                                type="integer"
                                className="context-view-editor__num"
                              />
                            </div>
                          )}

                          {/* What stands in for the values beyond `maxValues`. Paired with
                              `maxValues` in the design (ASPECT_MULTI_OPTIONS): capping the
                              list without being able to suppress the `+N` chip is only half
                              the control. */}
                          {boundOption && isChoiceAspectKind(boundOption.type) && (
                            <div className="context-view-editor__atlas-inspector-field">
                              <span className="context-view-editor__atlas-inspector-field-label">
                                {t('context.viewEditor.atlas.aspectPicker.overflow')}
                              </span>
                              <SegmentedControlAtom
                                mode="single"
                                variant="pills"
                                size="small"
                                value={(selectedCol.moleculeProps?.overflow as string) ?? 'count'}
                                options={[
                                  { value: 'count', label: t('context.viewEditor.atlas.aspectPicker.overflowCount') },
                                  { value: 'none', label: t('context.viewEditor.atlas.aspectPicker.overflowNone') },
                                ]}
                                onChange={(val) =>
                                  updateAtlasColumn(selectedAtlasColIdx!, {
                                    moleculeProps: { ...selectedCol.moleculeProps, overflow: val },
                                  })
                                }
                                ariaLabel={t('context.viewEditor.atlas.aspectPicker.overflow')}
                              />
                            </div>
                          )}
                        </div>
                      );
                    }

                    return (
                      <div key={prop.key} className="context-view-editor__atlas-inspector-field">
                        <span className="context-view-editor__atlas-inspector-field-label">{t(prop.i18nLabel)}</span>
                        <InputText
                          value={(rawValue as string) ?? (prop.defaultValue as string)}
                          onChange={(e) =>
                            updateAtlasColumn(selectedAtlasColIdx!, {
                              moleculeProps: { ...selectedCol.moleculeProps, [prop.key]: e.target.value },
                            })
                          }
                          disabled={disabled}
                          className="context-view-editor__input"
                        />
                      </div>
                    );
                  })}

                  <div className="context-view-editor__atlas-inspector-sep" />

                  {/* Always shown / Keep when tight */}
                  {ATLAS_MOLECULE_CATALOG[selectedCol.type]?.locked ? (
                    <div className="context-view-editor__atlas-info-box">
                      <i className="pi pi-lock" />
                      <div className="context-view-editor__atlas-info-box-content">
                        <div className="context-view-editor__atlas-info-box-title">{t('context.viewEditor.atlas.alwaysShown')}</div>
                        <div className="context-view-editor__atlas-info-box-desc">{t('context.viewEditor.atlas.alwaysShownDesc')}</div>
                      </div>
                    </div>
                  ) : (
                    <div className={`context-view-editor__atlas-info-box${selectedCol.moleculeProps?.important ? ' context-view-editor__atlas-info-box--keep' : ''}`}>
                      <i className="pi pi-thumbtack" />
                      <div className="context-view-editor__atlas-info-box-content">
                        <div className="context-view-editor__atlas-info-box-title">{t('context.viewEditor.atlas.keepWhenTight')}</div>
                        <div className="context-view-editor__atlas-info-box-desc">{t('context.viewEditor.atlas.keepWhenTightDesc')}</div>
                      </div>
                      <label className="context-view-editor__toggle-item context-view-editor__toggle-item--inline">
                        <input
                          type="checkbox"
                          className="context-view-editor__toggle-input"
                          checked={selectedCol.moleculeProps?.important === true}
                          onChange={(e) =>
                            updateAtlasColumn(selectedAtlasColIdx!, {
                              moleculeProps: { ...selectedCol.moleculeProps, important: e.target.checked },
                            })
                          }
                          disabled={disabled}
                          aria-label={t('context.viewEditor.atlas.keepWhenTight')}
                        />
                        <span className="context-view-editor__toggle-switch" />
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="context-view-editor__atlas-divider" />

        {/* Explore behavior */}
        <div className="context-view-editor__atlas-section">
          <span className="context-view-editor__atlas-section-label">
            {t('context.viewEditor.atlas.explore.title')}
          </span>
          <div className="context-view-editor__atlas-explore">
            <div className="context-view-editor__atlas-explore-item">
              <label className="context-view-editor__toggle-item context-view-editor__toggle-item--inline">
                <input
                  type="checkbox"
                  className="context-view-editor__toggle-input"
                  checked={config.allowTraversalDirectionChange !== false}
                  onChange={(e) => updateViewConfig({ allowTraversalDirectionChange: e.target.checked })}
                  disabled={disabled}
                  aria-label={t('context.viewEditor.atlas.explore.allowDirection')}
                />
                <span className="context-view-editor__toggle-switch" />
              </label>
              <div className="context-view-editor__atlas-explore-info">
                <span className="context-view-editor__atlas-explore-name">{t('context.viewEditor.atlas.explore.allowDirection')}</span>
                <span className="context-view-editor__atlas-explore-desc">{t('context.viewEditor.atlas.explore.allowDirectionDesc')}</span>
              </div>
            </div>
            <div className="context-view-editor__atlas-explore-item">
              <label className="context-view-editor__toggle-item context-view-editor__toggle-item--inline">
                <input
                  type="checkbox"
                  className="context-view-editor__toggle-input"
                  checked={config.lockFocus === true}
                  onChange={(e) => updateViewConfig({ lockFocus: e.target.checked })}
                  disabled={disabled}
                  aria-label={t('context.viewEditor.atlas.explore.lockCenter')}
                />
                <span className="context-view-editor__toggle-switch" />
              </label>
              <div className="context-view-editor__atlas-explore-info">
                <span className="context-view-editor__atlas-explore-name">{t('context.viewEditor.atlas.explore.lockCenter')}</span>
                <span className="context-view-editor__atlas-explore-desc">{t('context.viewEditor.atlas.explore.lockCenterDesc')}</span>
              </div>
            </div>
            {/*
              `hideAddSphere` is honoured by the renderer (AtlasTree.tsx) but the hand-off
              shows no control for it (spec settings table). Grouped with explore behaviour
              rather than built-in elements: it governs an explore-time affordance, not a
              rendered column.
            */}
            <div className="context-view-editor__atlas-explore-item">
              <label className="context-view-editor__toggle-item context-view-editor__toggle-item--inline">
                <input
                  type="checkbox"
                  className="context-view-editor__toggle-input"
                  checked={config.hideAddSphere === true}
                  onChange={(e) => updateViewConfig({ hideAddSphere: e.target.checked })}
                  disabled={disabled}
                  aria-label={t('context.viewEditor.atlas.hideAddSphere')}
                />
                <span className="context-view-editor__toggle-switch" />
              </label>
              <div className="context-view-editor__atlas-explore-info">
                <span className="context-view-editor__atlas-explore-name">{t('context.viewEditor.atlas.hideAddSphere')}</span>
                <span className="context-view-editor__atlas-explore-desc">{t('context.viewEditor.atlas.hideAddSphereDesc')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderKanbanConfig = () => {
    if (value.viewConfig.type !== 'kanban') return null;
    const config = value.viewConfig;

    return (
      <>
        <FieldRow label={t('context.viewEditor.kanban.laneProperty')}>
          <InputText
            value={config.laneProperty}
            onChange={(e) => updateViewConfig({ laneProperty: e.target.value })}
            placeholder={t('context.viewEditor.kanban.lanePropertyPlaceholder')}
            disabled={disabled}
            className="context-view-editor__input"
          />
        </FieldRow>
        <FieldRow label={t('context.viewEditor.kanban.swimlaneProperty')}>
          <InputText
            value={config.swimlaneProperty || ''}
            onChange={(e) => updateViewConfig({ swimlaneProperty: e.target.value || undefined })}
            placeholder={t('context.viewEditor.kanban.swimlanePropertyPlaceholder')}
            disabled={disabled}
            className="context-view-editor__input"
          />
        </FieldRow>
        <FieldRow label={t('context.viewEditor.kanban.cardSize')}>
          <DropdownAtom
            value={config.cardSize || 'standard'}
            options={CARD_SIZE_OPTIONS}
            onChange={(e) => updateViewConfig({ cardSize: e.value })}
            disabled={disabled}
            className="context-view-editor__input"
          />
        </FieldRow>
        <FieldRow label={t('context.viewEditor.kanban.cardFields')}>
          <MultiSelect
            value={config.cardFields || []}
            options={propertyOptions}
            onChange={(e) => updateViewConfig({ cardFields: e.value })}
            placeholder={t('context.viewEditor.kanban.selectFields')}
            disabled={disabled}
            display="chip"
            className="context-view-editor__input"
          />
        </FieldRow>
      </>
    );
  };

  const renderTableConfig = () => {
    if (value.viewConfig.type !== 'table') return null;
    const config = value.viewConfig;

    return (
      <>
        <FieldRow label={t('context.viewEditor.table.rowHeight')}>
          <DropdownAtom
            value={config.rowHeight || 'standard'}
            options={ROW_HEIGHT_OPTIONS}
            onChange={(e) => updateViewConfig({ rowHeight: e.value })}
            disabled={disabled}
            className="context-view-editor__input"
          />
        </FieldRow>
        <FieldRow label={t('context.viewEditor.table.groupBy')}>
          <InputText
            value={config.groupBy || ''}
            onChange={(e) => updateViewConfig({ groupBy: e.target.value || undefined })}
            placeholder={t('context.viewEditor.table.groupByPlaceholder')}
            disabled={disabled}
            className="context-view-editor__input"
          />
        </FieldRow>
        <div className="context-view-editor__columns">
          <div className="context-view-editor__columns-header">
            <span className="context-view-editor__row-label-text">{t('context.viewEditor.table.columns')}</span>
            <button
              type="button"
              className="context-view-editor__add-btn"
              onClick={addTableColumn}
              disabled={disabled}
            >
              <i className="pi pi-plus" /> {t('context.viewEditor.table.addColumn')}
            </button>
          </div>
          {config.columns.map((col, index) => (
            <div key={index} className="context-view-editor__column-row">
              <InputText
                value={col.property}
                onChange={(e) => updateTableColumn(index, { property: e.target.value })}
                placeholder={t('context.viewEditor.table.property')}
                disabled={disabled}
                className="context-view-editor__input context-view-editor__input--flex"
              />
              <LocalizedInputAtom
                value={col.header}
                onChange={(header) => updateTableColumn(index, { header })}
                label=""
                placeholder={t('context.viewEditor.table.header')}
                disabled={disabled}
              />
              <DropdownAtom
                value={col.renderer || 'text'}
                options={TABLE_RENDERER_OPTIONS}
                onChange={(e) => updateTableColumn(index, { renderer: e.value })}
                disabled={disabled}
                className="context-view-editor__input"
              />
              <button
                type="button"
                className="context-view-editor__remove-btn"
                onClick={() => removeTableColumn(index)}
                disabled={disabled}
                aria-label={t('context.viewEditor.table.removeColumnAriaLabel', { index: index + 1 })}
              >
                <i className="pi pi-trash" />
              </button>
            </div>
          ))}
          {config.columns.length === 0 && (
            <div className="context-view-editor__empty">{t('context.viewEditor.table.noColumns')}</div>
          )}
        </div>
      </>
    );
  };

  const renderTimelineConfig = () => {
    if (value.viewConfig.type !== 'timeline') return null;
    const config = value.viewConfig;

    return (
      <>
        <FieldRow label={t('context.viewEditor.timeline.startDateProperty')}>
          <InputText
            value={config.startDateProperty}
            onChange={(e) => updateViewConfig({ startDateProperty: e.target.value })}
            placeholder={t('context.viewEditor.timeline.startDatePlaceholder')}
            disabled={disabled}
            className="context-view-editor__input"
          />
        </FieldRow>
        <FieldRow label={t('context.viewEditor.timeline.endDateProperty')}>
          <InputText
            value={config.endDateProperty || ''}
            onChange={(e) => updateViewConfig({ endDateProperty: e.target.value || undefined })}
            placeholder={t('context.viewEditor.timeline.endDatePlaceholder')}
            disabled={disabled}
            className="context-view-editor__input"
          />
        </FieldRow>
        <FieldRow label={t('context.viewEditor.timeline.defaultScale')}>
          <DropdownAtom
            value={config.defaultScale || 'month'}
            options={TIME_SCALE_OPTIONS}
            onChange={(e) => updateViewConfig({ defaultScale: e.value })}
            disabled={disabled}
            className="context-view-editor__input"
          />
        </FieldRow>
      </>
    );
  };

  const renderScorecardConfig = () => {
    if (value.viewConfig.type !== 'scorecard') return null;
    const config = value.viewConfig;

    return (
      <div className="context-view-editor__section context-view-editor__section--grid">
        <div className="context-view-editor__field context-view-editor__field--checkbox">
          <Checkbox
            inputId="scorecardShowStatus"
            checked={config.surfaceColumns.showStatus}
            onChange={(e) => updateViewConfig({ surfaceColumns: { ...config.surfaceColumns, showStatus: e.checked ?? true } })}
            disabled={disabled}
          />
          <label htmlFor="scorecardShowStatus">{t('context.viewEditor.scorecard.showStatus')}</label>
        </div>
        <div className="context-view-editor__field context-view-editor__field--checkbox">
          <Checkbox
            inputId="scorecardShowVelocity"
            checked={config.surfaceColumns.showVelocity}
            onChange={(e) => updateViewConfig({ surfaceColumns: { ...config.surfaceColumns, showVelocity: e.checked ?? true } })}
            disabled={disabled}
          />
          <label htmlFor="scorecardShowVelocity">{t('context.viewEditor.scorecard.showVelocity')}</label>
        </div>
        <div className="context-view-editor__field context-view-editor__field--checkbox">
          <Checkbox
            inputId="scorecardShowGravity"
            checked={config.surfaceColumns.showGravity}
            onChange={(e) => updateViewConfig({ surfaceColumns: { ...config.surfaceColumns, showGravity: e.checked ?? true } })}
            disabled={disabled}
          />
          <label htmlFor="scorecardShowGravity">{t('context.viewEditor.scorecard.showGravity')}</label>
        </div>
        <div className="context-view-editor__field">
          <label className="context-view-editor__field-label">{t('context.viewEditor.scorecard.defaultSortColumn')}</label>
          <InputText
            value={config.defaultSort.column}
            onChange={(e) => updateViewConfig({ defaultSort: { ...config.defaultSort, column: e.target.value } })}
            placeholder={t('context.viewEditor.scorecard.defaultSortColumnPlaceholder')}
            disabled={disabled}
            className="context-view-editor__input"
          />
        </div>
        <div className="context-view-editor__field">
          <label className="context-view-editor__field-label">{t('context.viewEditor.scorecard.groupBy')}</label>
          <InputText
            value={config.groupBy || ''}
            onChange={(e) => updateViewConfig({ groupBy: e.target.value || undefined })}
            placeholder={t('context.viewEditor.scorecard.groupByPlaceholder')}
            disabled={disabled}
            className="context-view-editor__input"
          />
        </div>
        <div className="context-view-editor__field context-view-editor__field--checkbox">
          <Checkbox
            inputId="scorecardCardsDisplay"
            checked={config.display === 'cards'}
            onChange={(e) => updateViewConfig({ display: e.checked ? 'cards' : 'table' })}
            disabled={disabled}
          />
          <label htmlFor="scorecardCardsDisplay">{t('context.viewEditor.scorecard.defaultToCards')}</label>
        </div>
        <div className="context-view-editor__atlas-divider" />

      </div>
    );
  };

  const renderViewConfig = () => {
    switch (value.viewType) {
      case 'atlas': return renderAtlasConfig();
      case 'treemap': return renderTreeMapConfig();
      case 'kanban': return renderKanbanConfig();
      case 'table': return renderTableConfig();
      case 'timeline': return renderTimelineConfig();
      case 'scorecard': return renderScorecardConfig();
      default: return null;
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  // Annotated rather than inlined: StatusBadgeAtom's props are a discriminated
  // union (`status` vs `tone`), and the spec tsconfig compiles without
  // strictNullChecks, where the `tone?: never` / `status?: never` discriminants
  // stop narrowing and an inline ternary widens to `string`.
  const visibilityTone: StatusBadgeTone = value.listed ? 'success' : 'neutral';

  return (
    <div className={`context-view-editor ${className}`.trim()}>
      {/*
        A · Cover — Atlas has exactly one thing to configure (dev note 1); Description and
        Visibility live in the header instead (Phase 2). Gated on `value.viewType`, the same
        discriminant Cards C/E use, so it can never disagree with them mid type-switch.
      */}
      {value.viewType !== 'atlas' && (
      <ConfigCard
        letter="A"
        title={t('context.viewEditor.cover.title')}
        subtitle={t('context.viewEditor.cover.subtitle')}
      >
        <FieldRow label={t('context.viewEditor.description')}>
          <TextareaAtom
            value={value.description.default}
            onChange={(e) =>
              handleDescriptionChange({ ...value.description, default: e.target.value })
            }
            placeholder={t('context.viewEditor.viewDescription')}
            disabled={disabled}
            rows={2}
            className="context-view-editor__textarea"
          />
        </FieldRow>
        <FieldRow
          label={t('context.viewEditor.cover.rendersContext')}
          description={t('context.viewEditor.cover.rendersContextDesc')}
        >
          <span className="context-view-editor__locked-chip">
            <i className="pi pi-sitemap" aria-hidden="true" />
            {contextDisplayName || value.contextRef}
            <i className="pi pi-lock context-view-editor__locked-chip-lock" aria-hidden="true" />
          </span>
          {onOpenContext && (
            <button
              type="button"
              className="context-view-editor__open-context-link"
              onClick={onOpenContext}
            >
              <i className="pi pi-external-link" aria-hidden="true" />
              {t('context.viewEditor.cover.openContext', 'Open context')}
            </button>
          )}
        </FieldRow>
        <FieldRow
          label={t('context.viewEditor.cover.visibility')}
          description={
            value.listed
              ? t('context.viewEditor.cover.visibilityListedDesc')
              : t('context.viewEditor.cover.visibilityPrivateDesc')
          }
        >
          {/*
            `canPublish`/`onToggleListing` also now drive the header's Visibility chip
            (Phase 2), but this card still renders for non-Atlas view types per spec's
            Out-of-Scope — "Cards A/B/C/E continue to render... exactly as today" — so
            deleting the prop here would regress those types. Both consumers survive
            deliberately; Atlas users see only the header's copy because this card no
            longer renders for Atlas.
          */}
          <div className="context-view-editor__visibility">
            <StatusBadgeAtom
              tone={visibilityTone}
              label={
                value.listed
                  ? t('context.viewEditor.cover.listed')
                  : t('context.viewEditor.cover.private')
              }
              icon={value.listed ? 'pi pi-globe' : 'pi pi-lock'}
            />
            {canPublish && onToggleListing && (
              <ButtonAtom
                variant="secondary"
                size="small"
                label={
                  value.listed
                    ? t('context.viewEditor.cover.unlistAction')
                    : t('context.viewEditor.cover.listAction')
                }
                onClick={() => onToggleListing(!value.listed)}
                disabled={disabled}
              />
            )}
          </div>
        </FieldRow>
        {hasPersonalizationOverlay && onResetPersonalization && (
          <FieldRow
            label={t('context.viewEditor.cover.personalization')}
            description={t('context.viewEditor.cover.personalizationDesc')}
          >
            <div className="context-view-editor__visibility">
              <StatusBadgeAtom
                tone="info"
                label={t('context.viewEditor.cover.personalizedBadge')}
                icon="pi pi-user"
              />
              <ButtonAtom
                variant="secondary"
                size="small"
                label={t('context.viewEditor.cover.resetAdjustmentsAction')}
                onClick={() => setResetDialogVisible(true)}
                disabled={disabled}
              />
            </div>
            <DialogWrapper
              header={t('context.viewEditor.cover.resetAdjustmentsConfirm.header')}
              visible={resetDialogVisible}
              onHide={() => setResetDialogVisible(false)}
            >
              <p>{t('context.viewEditor.cover.resetAdjustmentsConfirm.message')}</p>
              <div className="context-view-editor__dialog-actions">
                <ButtonAtom
                  variant="secondary"
                  size="small"
                  label={t('context.viewEditor.cover.resetAdjustmentsConfirm.cancelAction')}
                  onClick={() => setResetDialogVisible(false)}
                />
                <ButtonAtom
                  variant="danger"
                  size="small"
                  label={t('context.viewEditor.cover.resetAdjustmentsConfirm.confirmAction')}
                  onClick={handleConfirmResetPersonalization}
                />
              </div>
            </DialogWrapper>
          </FieldRow>
        )}
      </ConfigCard>
      )}

      {/*
        B · View type — same Atlas gate as Card A (dev note 1: Atlas has exactly one
        thing to configure). The header already shows the current type as a locked badge.
      */}
      {value.viewType !== 'atlas' && (
      <ConfigCard
        letter="B"
        title={t('context.viewEditor.viewType')}
        subtitle={
          lockViewType
            ? t('context.viewEditor.viewTypeCard.lockedSubtitle')
            : t('context.viewEditor.viewTypeCard.subtitle')
        }
      >
        {lockViewType ? (
          <div className="context-view-editor__locked-type">
            <span className="context-view-editor__locked-type-icon" aria-hidden="true">
              <i className={VIEW_TYPE_META[value.viewType].icon} />
            </span>
            <div className="context-view-editor__locked-type-text">
              <div className="context-view-editor__locked-type-title">
                {viewTypeLabel}
                <i className="pi pi-lock" aria-hidden="true" />
              </div>
              <div className="context-view-editor__locked-type-desc">
                {t('context.viewEditor.viewTypeCard.lockedHint')}
              </div>
            </div>
          </div>
        ) : (
          <div className="context-view-editor__type-tiles">
            {VIEW_TYPE_OPTIONS.map((opt) => {
              const active = opt.value === value.viewType;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`context-view-editor__type-tile ${active ? 'context-view-editor__type-tile--active' : ''}`}
                  onClick={() => handleViewTypeChange(opt.value)}
                  disabled={disabled}
                  aria-pressed={active}
                >
                  <span className="context-view-editor__type-tile-icon" aria-hidden="true">
                    <i className={opt.icon} />
                  </span>
                  <span className="context-view-editor__type-tile-label">{opt.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </ConfigCard>
      )}

      {/*
        C · Base config — Card Density / Visible Properties / Always Show describe a card
        grid. Atlas is a tree, not a card grid; the dev handoff forbids restoring this card
        for it ("those describe a card grid and are meaningless for a tree").
      */}
      {value.viewType !== 'atlas' && (
      <ConfigCard
        letter="C"
        title={t('context.viewEditor.baseConfiguration')}
        subtitle={t('context.viewEditor.baseConfigCard.subtitle')}
      >
        <FieldRow label={t('context.viewEditor.cardDensity')}>
          <SegmentedControlAtom
            mode="single"
            size="small"
            options={CARD_DENSITY_OPTIONS}
            value={cardDensity}
            onChange={(v) => setCardDensity(v as CardViewDensity)}
            ariaLabel={t('context.viewEditor.cardDensity')}
          />
        </FieldRow>
        <FieldRow
          label={t('context.viewEditor.visibleProperties')}
          description={t('context.viewEditor.visiblePropertiesDesc')}
        >
          <div className="context-view-editor__chips">
            {visibleProperties.map((property) => (
              <span key={property} className="context-view-editor__prop-chip">
                <span className="context-view-editor__prop-chip-label">{property}</span>
                {!disabled && (
                  <button
                    type="button"
                    className="context-view-editor__prop-chip-remove"
                    onClick={() => removeVisibleProperty(property)}
                    aria-label={t('context.viewEditor.removePropertyAriaLabel', { property })}
                  >
                    <i className="pi pi-times" />
                  </button>
                )}
              </span>
            ))}
            {addingProperty ? (
              <span className="context-view-editor__add-property">
                {remainingPropertyOptions.length > 0 ? (
                  <DropdownAtom
                    value={null}
                    options={remainingPropertyOptions}
                    onChange={(e) => {
                      if (e.value) {
                        updateBaseConfig({ visibleProperties: [...visibleProperties, e.value] });
                      }
                      setAddingProperty(false);
                    }}
                    placeholder={t('context.viewEditor.selectProperties')}
                    className="context-view-editor__input"
                  />
                ) : (
                  <InputText
                    value={newProperty}
                    autoFocus
                    onChange={(e) => setNewProperty(e.target.value)}
                    onBlur={commitNewProperty}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitNewProperty();
                      if (e.key === 'Escape') {
                        setNewProperty('');
                        setAddingProperty(false);
                      }
                    }}
                    placeholder={t('context.viewEditor.propertyNamePlaceholder')}
                    className="context-view-editor__input context-view-editor__add-property-input"
                  />
                )}
              </span>
            ) : (
              !disabled && (
                <button
                  type="button"
                  className="context-view-editor__add-chip"
                  onClick={() => setAddingProperty(true)}
                >
                  <i className="pi pi-plus" /> {t('context.viewEditor.addProperty')}
                </button>
              )
            )}
          </div>
        </FieldRow>
        <FieldRow
          label={t('context.viewEditor.alwaysShow')}
          description={t('context.viewEditor.alwaysShowDesc')}
        >
          <div className="context-view-editor__toggles-group">
            <label className="context-view-editor__toggle-item">
              <input
                type="checkbox"
                className="context-view-editor__toggle-input"
                checked={value.baseConfig.showStatus !== false}
                onChange={(e) => updateBaseConfig({ showStatus: e.target.checked })}
                disabled={disabled}
              />
              <span className="context-view-editor__toggle-switch"></span>
              <span className="context-view-editor__toggle-label">{t('context.viewEditor.showStatus')}</span>
            </label>
            <label className="context-view-editor__toggle-item">
              <input
                type="checkbox"
                className="context-view-editor__toggle-input"
                checked={value.baseConfig.showFlow !== false}
                onChange={(e) => updateBaseConfig({ showFlow: e.target.checked })}
                disabled={disabled}
              />
              <span className="context-view-editor__toggle-switch"></span>
              <span className="context-view-editor__toggle-label">{t('context.viewEditor.showFlow')}</span>
            </label>
            <label className="context-view-editor__toggle-item">
              <input
                type="checkbox"
                className="context-view-editor__toggle-input"
                checked={value.baseConfig.showAvatar !== false}
                onChange={(e) => updateBaseConfig({ showAvatar: e.target.checked })}
                disabled={disabled}
              />
              <span className="context-view-editor__toggle-switch"></span>
              <span className="context-view-editor__toggle-label">{t('context.viewEditor.showAvatar')}</span>
            </label>
          </div>
        </FieldRow>
      </ConfigCard>
      )}

      {/*
        D · Type-specific config — Atlas's only card, unlettered per SC1 (no letter badge);
        every other type keeps its letter.
      */}
      <ConfigCard
        letter={value.viewType === 'atlas' ? undefined : 'D'}
        title={
          value.viewType === 'atlas'
            ? t('context.viewEditor.atlas.columnsTitle')
            : t('context.viewEditor.viewConfiguration', { viewType: viewTypeLabel })
        }
        subtitle={
          value.viewType === 'atlas'
            ? t('context.viewEditor.atlas.columnsSubtitle')
            : t('context.viewEditor.typeConfigCard.subtitle')
        }
        action={
          value.viewType === 'atlas' && value.viewConfig.type === 'atlas' ? (
            <span className="context-view-editor__atlas-count-badge">
              {t('context.viewEditor.atlas.columnsCount', { count: (value.viewConfig.nodeMolecules ?? []).length })}
            </span>
          ) : undefined
        }
      >
        {renderViewConfig()}
      </ConfigCard>

      {/*
        E · Level overrides — treemap/table/kanban only; not applicable to Atlas.
        Gated on `value.viewType`, the same discriminant Card C uses (see above), so the
        two can never disagree mid type-switch — `viewType` flips atomically with the user's
        selection, while `viewConfig.type` briefly lags it during `handleViewTypeChange`.
      */}
      {levels.length > 0 && value.viewType !== 'atlas' && (
        <ConfigCard
          letter="E"
          title={t('context.viewEditor.levelOverrides.title')}
          subtitle={t('context.viewEditor.levelOverrides.subtitle')}
        >
          <div className="context-view-editor__level-overrides">
            {/* A plain toggle group, not role="tablist": these buttons use
                aria-pressed, which is invalid on tablist children, and real tab
                semantics would additionally require roving tabindex + arrow-key
                navigation. role="group" describes what this actually is. */}
            <div
              className="context-view-editor__levels-rail"
              role="group"
              aria-label={t('context.viewEditor.levelOverrides.title')}
            >
              {levels.map((level) => {
                const overrideCount = Object.keys(levelOverrides[level.id] ?? {}).length;
                const active = selectedLevel?.id === level.id;
                return (
                  <button
                    key={level.id}
                    type="button"
                    className={`context-view-editor__level-row ${active ? 'context-view-editor__level-row--active' : ''}`}
                    onClick={() => setSelectedLevelId(level.id)}
                    aria-pressed={active}
                  >
                    <ContextLevelSummaryAtom level={level} compact />
                    {overrideCount > 0 ? (
                      <NotificationBadgeAtom count={overrideCount} severity="info" />
                    ) : (
                      <span className="context-view-editor__level-row-empty">
                        {t('context.viewEditor.levelOverrides.noOverrides')}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {/* `selectedLevel` falls back to levels[0] and this whole card only
                renders when levels.length > 0, so there is no "pick a level"
                empty state to show — the first level is always pre-selected. */}
            <div className="context-view-editor__level-override-body">
              {selectedLevel && (
                <LevelOverrideEditorMolecule
                  override={levelOverrides[selectedLevel.id] ?? {}}
                  propertyOptions={propertyOptions}
                  onFieldChange={(field, fieldValue) =>
                    updateLevelOverrideField(selectedLevel.id, field, fieldValue)
                  }
                  onResetAll={() => resetLevelOverride(selectedLevel.id)}
                  disabled={disabled}
                />
              )}
            </div>
          </div>
        </ConfigCard>
      )}
    </div>
  );
};

export default ContextViewEditorOrganism;
